import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Search, Plus, Receipt, Download, CheckCircle, Clock, AlertTriangle, Euro, FileText, Eye, Zap, Calendar, CalendarDays, Users, Loader2, Trash2, Send, XCircle, CreditCard, Ban, Pencil, Combine, Settings2, Edit2, Check, Copy, Info, Bell, ClipboardList } from 'lucide-react';
import axios from 'axios';
import { DecompteTab } from '../components/billing/DecompteTab';
import { PricingRulesTable } from '../components/billing/PricingRulesTable';
import { CavalierDecompteView } from '../components/billing/CavalierDecompteView';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ TARIFS DATA ============

// Règles de facturation complètes par type de cavalier (avec périodicité intégrée)
const reglesFacturationParRole = {
  cavalier_proprio: {
    label: "Cavalier Propriétaire",
    color: "amber",
    prestations: [
      {
        nom: "Pension mensuelle",
        description: "Hébergement du cheval au club (box ou pré)",
        periodicite: "Mensuelle",
        echeance: "Début de mois",
        tarifs: ["Box poney: 325€", "Box cheval paille: 390€", "Box cheval copeaux: 430€", "Pré: 245€"]
      },
      {
        nom: "Cours collectifs",
        description: "Tarif préférentiel pour les propriétaires",
        periodicite: "Trimestrielle",
        echeance: "Fin de trimestre",
        tarifs: ["19€ par cours (tarif préférentiel)", "Cours comptés et facturés en fin de trimestre"]
      },
      {
        nom: "Inscriptions annuelles",
        description: "Licence et cotisation club",
        periodicite: "Annuelle",
        echeance: "À l'inscription",
        tarifs: ["Licence FFE +18 ans: 40€", "Licence FFE -18 ans: 29€", "Cotisation club: 100€"]
      }
    ]
  },
  cavalier_dp: {
    label: "Cavalier Demi-Pension",
    color: "blue",
    prestations: [
      {
        nom: "Forfait Demi-Pension",
        description: "Demi-pension d'un cheval du club",
        periodicite: "Mensuelle",
        echeance: "Début de mois",
        tarifs: ["220€/mois tout compris"],
        inclus: ["2 cours collectifs par semaine", "1 séance de travail à pied par semaine", "Accès au cheval en demi-pension"]
      },
      {
        nom: "Inscriptions annuelles",
        description: "Licence et cotisation club",
        periodicite: "Annuelle",
        echeance: "À l'inscription",
        tarifs: ["Licence FFE +18 ans: 40€", "Licence FFE -18 ans: 29€", "Cotisation club: 100€"]
      }
    ]
  },
  cavalier_forfait: {
    label: "Cavalier Forfait",
    color: "green",
    prestations: [
      {
        nom: "Forfait cours trimestriel",
        description: "Cours hebdomadaires inclus dans un forfait",
        periodicite: "Trimestrielle",
        echeance: "Début de trimestre",
        tarifs: ["Forfait 1 cours/sem: 335€/trim", "Forfait 2 cours/sem: 600€/trim", "Forfait Baby poney: 265€/trim"]
      },
      {
        nom: "Inscriptions annuelles",
        description: "Licence et cotisation club",
        periodicite: "Annuelle",
        echeance: "À l'inscription",
        tarifs: ["Licence FFE +18 ans: 40€", "Licence FFE -18 ans: 29€", "Cotisation club: 100€"]
      }
    ]
  },
  cavalier_pp: {
    label: "Cavalier Propriétaire Partagé",
    color: "purple",
    prestations: [
      {
        nom: "Cours collectifs",
        description: "Tarif préférentiel propriétaire partagé",
        periodicite: "Trimestrielle",
        echeance: "Fin de trimestre",
        tarifs: ["19€ par cours (tarif préférentiel)", "Cours comptés et facturés en fin de trimestre"]
      },
      {
        nom: "Inscriptions annuelles",
        description: "Licence et cotisation club",
        periodicite: "Annuelle",
        echeance: "À l'inscription",
        tarifs: ["Licence FFE +18 ans: 40€", "Licence FFE -18 ans: 29€", "Cotisation club: 100€"]
      }
    ]
  }
};

// Structure des tarifs par défaut
const defaultTarifs = {
  pensions: {
    label: "Pensions",
    description: "Facturation mensuelle, début de mois",
    items: {
      box_poney: { label: "Box Poney", prix: 325, note: "Tarif mensuel" },
      box_cheval_paille: { label: "Box Cheval Paille", prix: 390, note: "Tarif mensuel" },
      box_cheval_copeaux: { label: "Box Cheval Copeaux", prix: 430, note: "Tarif mensuel" },
      pre: { label: "Pré", prix: 245, note: "Tarif mensuel" }
    }
  },
  demi_pensions: {
    label: "Demi-Pensions",
    description: "Facturation mensuelle, début de mois",
    items: {
      dp_standard: { label: "Demi-pension standard", prix: 220, note: "Inclut 2 cours/sem + 1 travail à pied" }
    }
  },
  forfaits: {
    label: "Forfaits Trimestriels",
    description: "Facturation en début de trimestre (Oct, Jan, Avril)",
    items: {
      forfait_1_cours_moins_18: { label: "Forfait 1 cours/sem < 18 ans", prix: 335, note: "Trimestriel" },
      forfait_1_cours_plus_18: { label: "Forfait 1 cours/sem >= 18 ans", prix: 365, note: "Trimestriel" },
      forfait_2_cours_moins_18: { label: "Forfait 2 cours/sem < 18 ans", prix: 550, note: "Trimestriel" },
      forfait_2_cours_plus_18: { label: "Forfait 2 cours/sem >= 18 ans", prix: 600, note: "Trimestriel" },
      forfait_baby: { label: "Forfait Baby Poney", prix: 265, note: "Trimestriel" }
    }
  },
  cours_hebdo: {
    label: "Cours Hebdomadaires",
    description: "Facturation trimestrielle, fin de trimestre",
    items: {
      cours_dp: { label: "Tarif DP (hors forfait)", prix: 25, note: "Par cours" },
      cours_forfait: { label: "Tarif Forfait (hors forfait)", prix: 28, note: "Par cours" },
      cours_proprietaire: { label: "Tarif Propriétaire/PP", prix: 19, note: "Par cours" }
    }
  },
  stages: {
    label: "Stages",
    description: "Facturation à l'inscription",
    items: {
      stage_journee: { label: "Stage journée", prix: 65, note: "Par jour" },
      stage_demi_journee: { label: "Stage demi-journée", prix: 38, note: "Par demi-journée" }
    }
  },
  competitions: {
    label: "Compétitions",
    description: "Facturation à l'inscription",
    items: {
      inscription_concours: { label: "Inscription concours", prix: 35, note: "Par épreuve" },
      transport_van: { label: "Transport van", prix: 50, note: "Par trajet A/R" }
    }
  },
  inscriptions: {
    label: "Inscriptions Annuelles",
    description: "Facturation à l'inscription (Septembre)",
    items: {
      licence_ffe_adulte: { label: "Licence FFE +18 ans", prix: 40, note: "Annuelle" },
      licence_ffe_mineur: { label: "Licence FFE -18 ans", prix: 29, note: "Annuelle" },
      cotisation_club: { label: "Cotisation club", prix: 100, note: "Annuelle" }
    }
  }
};

// ============ FACTURATION DATA ============

// Calcule le prochain trimestre et l'année de saison
// T4: Oct-Déc (début de saison), T1: Jan-Mars, T2: Avril-Juin
const getNextTrimestreAndYear = () => {
  const now = new Date();
  const month = now.getMonth() + 1; // 1-12
  const year = now.getFullYear();
  
  // Déterminer le prochain trimestre à facturer
  if (month >= 1 && month <= 3) {
    // On est en T1 (Jan-Mars), prochain = T2 (Avril-Juin)
    return { trimestre: 'T2', annee: year - 1 }; // Saison a commencé en Oct année précédente
  } else if (month >= 4 && month <= 6) {
    // On est en T2 (Avril-Juin), prochain = T4 (Oct-Déc) nouvelle saison
    return { trimestre: 'T4', annee: year }; // Nouvelle saison commence en Oct cette année
  } else if (month >= 7 && month <= 9) {
    // Hors saison (Juillet-Sept), prochain = T4 (Oct-Déc)
    return { trimestre: 'T4', annee: year }; // Saison commence en Oct cette année
  } else {
    // On est en T4 (Oct-Déc), prochain = T1 (Jan-Mars)
    return { trimestre: 'T1', annee: year }; // Saison a commencé en Oct cette année
  }
};

const invoiceTypes = [
  { value: 'pension', label: 'Pension mensuelle' },
  { value: 'cours', label: 'Cours' },
  { value: 'stage', label: 'Stage' },
  { value: 'competition', label: 'Compétition' },
  { value: 'demi_pension', label: 'Demi-pension' },
  { value: 'licence', label: 'Licence + inscription' },
  { value: 'mixte', label: 'Mixte' },
  { value: 'fusion', label: 'Fusion' },
];

const paymentMethods = [
  { value: 'cb', label: 'Carte bancaire' },
  { value: 'cheque', label: 'Chèque' },
  { value: 'especes', label: 'Espèces' },
  { value: 'virement', label: 'Virement' },
];

// Labels for role_club
const roleLabels = {
  'cavalier_proprio': 'Propriétaire',
  'cavalier_pp': 'Proprio. Partagé',
  'cavalier_dp': 'Demi-pension',
  'cavalier_forfait': 'Forfait',
  'cavalier': 'Cavalier',
  'instructeur': 'Instructeur',
  'instructeur_admin': 'Admin',
};

const getRoleLabel = (role) => {
  if (!role) return 'N/A';
  return roleLabels[role] || role.replace('cavalier_', '').replace('_', ' ');
};

// Filter prestations based on user role
// Rules:
// - Pensions: ONLY for cavalier_proprio
// - Demi-pensions: ONLY for cavalier_dp
// - Forfaits: ONLY for cavalier_forfait
// - cours_hebdo items are role-specific:
//   - cavalier_dp: tarif DP
//   - cavalier_forfait: tarif forfait
//   - proprietaire: for cavalier_proprio AND cavalier_pp (tarif préférentiel)
// - cavalier_pp sees: cours préférentiels, stages, compétitions, etc. (EXCEPT pensions, demi-pensions, forfaits)
const getFilteredPrestations = (allPrestations, userRole) => {
  if (!userRole || !allPrestations.length) return allPrestations;
  
  return allPrestations.filter(p => {
    const cat = p.category?.toLowerCase() || '';
    const key = p.key?.toLowerCase() || '';
    const label = p.label?.toLowerCase() || '';
    
    // Pensions: ONLY for cavalier_proprio (Propriétaires)
    if (cat === 'pensions') {
      return userRole === 'cavalier_proprio';
    }
    
    // Demi-pensions: ONLY for cavalier_dp (Demi-pensionnaires)
    if (cat === 'demi_pension') {
      return userRole === 'cavalier_dp';
    }
    
    // Forfaits trimestriels: ONLY for cavalier_forfait
    if (cat.startsWith('forfaits_')) {
      return userRole === 'cavalier_forfait';
    }
    
    // cours_hebdo: Filter role-specific items
    if (cat === 'cours_hebdo') {
      // "Cavalier DP" items: only for cavalier_dp
      if (key === 'cavalier_dp' || label.includes('cavalier dp')) {
        return userRole === 'cavalier_dp';
      }
      // "Cavalier forfait" items: only for cavalier_forfait
      if (key === 'cavalier_forfait' || label.includes('cavalier forfait')) {
        return userRole === 'cavalier_forfait';
      }
      // "Propriétaire" items: for cavalier_proprio AND cavalier_pp (propriétaires partagés)
      if (key === 'proprietaire' || (label.includes('propriétaire') && !label.includes('toute'))) {
        return userRole === 'cavalier_proprio' || userRole === 'cavalier_pp';
      }
      // Generic items (cavalier_simple, toute catégorie): available to all
      return true;
    }
    
    // All other categories available to everyone
    return true;
  });
};

// Category labels for display
const categoryLabels = {
  'pensions': '🏠 Pensions',
  'demi_pension': '🐴 Demi-pensions',
  'cours_hebdo': '📅 Cours hebdomadaires',
  'cours_ponctuels': '📍 Cours ponctuels',
  'cours_particuliers': '👤 Cours particuliers',
  'forfaits_t1': '📋 Forfaits T1 (Oct-Déc)',
  'forfaits_t2': '📋 Forfaits T2 (Jan-Mars)',
  'forfaits_t4': '📋 Forfaits T4 (Avril-Juin)',
  'stages': '🏕️ Stages',
  'competitions': '🏆 Compétitions',
  'inscriptions': '📝 Inscriptions & Licences',
  'baptemes': '🎠 Baptêmes',
};

// Group prestations by category for display
const groupPrestationsByCategory = (prestations) => {
  const groups = {};
  prestations.forEach(p => {
    const cat = p.category || 'autres';
    if (!groups[cat]) {
      groups[cat] = [];
    }
    groups[cat].push(p);
  });
  
  // Sort categories in a logical order
  const categoryOrder = [
    'pensions', 'demi_pension', 
    'forfaits_t1', 'forfaits_t2', 'forfaits_t4',
    'cours_hebdo', 'cours_ponctuels', 'cours_particuliers',
    'stages', 'competitions', 'baptemes', 'inscriptions'
  ];
  
  const sortedGroups = [];
  categoryOrder.forEach(cat => {
    if (groups[cat] && groups[cat].length > 0) {
      sortedGroups.push({
        category: cat,
        label: categoryLabels[cat] || cat,
        items: groups[cat]
      });
    }
  });
  
  // Add any remaining categories not in the order
  Object.keys(groups).forEach(cat => {
    if (!categoryOrder.includes(cat) && groups[cat].length > 0) {
      sortedGroups.push({
        category: cat,
        label: categoryLabels[cat] || cat,
        items: groups[cat]
      });
    }
  });
  
  return sortedGroups;
};

const statusConfig = {
  brouillon: { label: 'Brouillon', color: 'bg-stone-100 text-stone-800', icon: FileText },
  en_attente: { label: 'En attente', color: 'bg-blue-100 text-blue-800', icon: Clock },
  payee_partiellement: { label: 'Payée partiellement', color: 'bg-amber-100 text-amber-800', icon: AlertTriangle },
  payee: { label: 'Payée', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  solde: { label: 'Soldé', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  en_retard: { label: 'En retard', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  annulee: { label: 'Annulée', color: 'bg-stone-100 text-stone-500', icon: FileText },
};

// Types de génération automatique
const generationTypes = [
  { 
    value: 'pensions_mensuelles', 
    label: 'Pensions mensuelles', 
    description: 'Facture les pensions des propriétaires',
    icon: '🏠',
    timing: 'Début de mois'
  },
  { 
    value: 'demi_pensions', 
    label: 'Demi-pensions mensuelles', 
    description: 'Facture les demi-pensions',
    icon: '🐴',
    timing: 'Début de mois'
  },
  { 
    value: 'forfaits_trimestriels', 
    label: 'Forfaits trimestriels', 
    description: 'Facture les forfaits des cavaliers',
    icon: '📋',
    timing: 'Début de trimestre'
  },
  { 
    value: 'cours_proprietaires', 
    label: 'Cours propriétaires', 
    description: 'Facture les cours des propriétaires',
    icon: '🏇',
    timing: 'Fin de trimestre'
  },
  { 
    value: 'cours_dp_hors_forfait', 
    label: 'Cours DP hors forfait', 
    description: 'Facture les cours excédentaires des DP',
    icon: '📊',
    timing: 'Fin de trimestre'
  },
];

export const Billing = () => {
  const { user, isInstructor, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Main tab: 'factures', 'tarifs', or 'decompte'
  const [mainTab, setMainTab] = useState(searchParams.get('tab') || 'decompte'); // Default to decompte
  const [defaultBillingView, setDefaultBillingView] = useState(null);
  const [facturationEnabled, setFacturationEnabled] = useState(false); // Module Facturation disabled by default
  
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'facture', 'avoir'
  const [activeTab, setActiveTab] = useState('list');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedFacture, setSelectedFacture] = useState(null);
  const [users, setUsers] = useState([]);
  
  // States for tarifs
  const [baremes, setBaremes] = useState([]);
  const [currentBareme, setCurrentBareme] = useState(null);
  const [loadingBaremes, setLoadingBaremes] = useState(true);
  const [isBaremeDialogOpen, setIsBaremeDialogOpen] = useState(false);
  const [isBaremeEditMode, setIsBaremeEditMode] = useState(false);
  const [editingBareme, setEditingBareme] = useState(null);
  const [copyFromPrevious, setCopyFromPrevious] = useState(false);
  const [newBareme, setNewBareme] = useState({
    saison: '',
    date_debut: '',
    date_fin: '',
    statut: 'brouillon',
    tarifs: JSON.parse(JSON.stringify(defaultTarifs))
  });
  
  // States for automatic billing - initialiser avec le prochain trimestre
  const defaultTrimestreData = getNextTrimestreAndYear();
  const [isGenerationDialogOpen, setIsGenerationDialogOpen] = useState(false);
  const [generationType, setGenerationType] = useState('');
  const [generationMois, setGenerationMois] = useState(new Date().toISOString().slice(0, 7));
  const [generationTrimestre, setGenerationTrimestre] = useState(defaultTrimestreData.trimestre);
  const [generationAnnee, setGenerationAnnee] = useState(defaultTrimestreData.annee);
  const [apercu, setApercu] = useState(null);
  const [isLoadingApercu, setIsLoadingApercu] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  
  const [newFacture, setNewFacture] = useState({
    cavalier_id: '',
    type_facture: 'cours',
    date_emission: new Date().toISOString().split('T')[0],
    date_echeance: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    lignes: [{ prestation: '', quantite: 1, prix_unitaire: 0, total_ligne: 0 }],
    notes: ''
  });
  
  // Prestations from barème
  const [prestations, setPrestations] = useState([]);
  const [loadingPrestations, setLoadingPrestations] = useState(false);
  
  // Edit mode for draft invoices
  const [editingFacture, setEditingFacture] = useState(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  
  // Merge invoices state
  const [mergeMode, setMergeMode] = useState(false);
  const [selectedForMerge, setSelectedForMerge] = useState([]);
  const [isMerging, setIsMerging] = useState(false);
  
  // Prestations non facturées (hors forfait)
  const [prestationsNonFacturees, setPrestationsNonFacturees] = useState(null);
  const [loadingPrestationsNonFacturees, setLoadingPrestationsNonFacturees] = useState(false);
  const [selectedPrestations, setSelectedPrestations] = useState({}); // { category: [ids] }
  
  // Manual billing management
  const [showCoursFacturationDialog, setShowCoursFacturationDialog] = useState(false);
  const [coursFacturationData, setCoursFacturationData] = useState(null);
  const [loadingCoursFacturation, setLoadingCoursFacturation] = useState(false);
  const [selectedCavalierForBilling, setSelectedCavalierForBilling] = useState(null);

  // Modal création avoir
  const [showAvoirModal, setShowAvoirModal] = useState(false);
  const [avoirFacture, setAvoirFacture] = useState(null);
  const [isCreatingAvoir, setIsCreatingAvoir] = useState(false);

  // Toggle selection of a prestation
  const togglePrestationSelection = (category, itemId) => {
    setSelectedPrestations(prev => {
      const categoryItems = prev[category] || [];
      if (categoryItems.includes(itemId)) {
        return { ...prev, [category]: categoryItems.filter(id => id !== itemId) };
      } else {
        return { ...prev, [category]: [...categoryItems, itemId] };
      }
    });
  };

  // Select all prestations in a category
  const selectAllInCategory = (category, items) => {
    setSelectedPrestations(prev => ({
      ...prev,
      [category]: items.map(item => item.id)
    }));
  };

  // Deselect all prestations in a category
  const deselectAllInCategory = (category) => {
    setSelectedPrestations(prev => ({
      ...prev,
      [category]: []
    }));
  };

  // Select all prestations across all categories
  const selectAllPrestations = () => {
    if (!prestationsNonFacturees?.prestations) return;
    const newSelection = {};
    Object.entries(prestationsNonFacturees.prestations).forEach(([category, data]) => {
      newSelection[category] = data.items.map(item => item.id);
    });
    setSelectedPrestations(newSelection);
  };

  // Deselect all prestations
  const deselectAllPrestations = () => {
    setSelectedPrestations({});
  };

  // Get count of selected prestations
  const getSelectedCount = () => {
    return Object.values(selectedPrestations).flat().length;
  };

  // Get IDs of prestations already in invoice lines (to prevent duplicates)
  const getAlreadyInvoicedIds = () => {
    const ids = new Set();
    newFacture.lignes.forEach(ligne => {
      if (ligne.cours_ids && Array.isArray(ligne.cours_ids)) {
        ligne.cours_ids.forEach(id => ids.add(id));
      }
    });
    return ids;
  };

  // Check if a prestation is already in invoice lines
  const isPrestationInInvoice = (itemId) => {
    return getAlreadyInvoicedIds().has(itemId);
  };

  // Get count of available (not yet invoiced) prestations
  const getAvailableCount = (category, items) => {
    const invoicedIds = getAlreadyInvoicedIds();
    return items.filter(item => !invoicedIds.has(item.id)).length;
  };

  // Generate invoice lines from selected prestations
  const generateLignesFromSelection = () => {
    if (!prestationsNonFacturees?.prestations) return;
    
    const newLignes = [];
    const selectedUser = users.find(u => u.id === newFacture.cavalier_id);
    const userRole = selectedUser?.role_club;
    
    Object.entries(selectedPrestations).forEach(([category, selectedIds]) => {
      if (!selectedIds.length) return;
      
      const categoryData = prestationsNonFacturees.prestations[category];
      if (!categoryData) return;
      
      const selectedItems = categoryData.items.filter(item => selectedIds.includes(item.id));
      if (!selectedItems.length) return;
      
      // Determine price based on category and role
      let prix = 0;
      let prestationLabel = '';
      
      switch (category) {
        case 'cours_preferentiel':
          // Find the preferential tariff from barème
          const tarifPref = prestations.find(p => 
            p.category === 'cours_hebdo' && 
            (p.key === 'proprietaire' || p.label?.toLowerCase().includes('propriétaire'))
          );
          prix = tarifPref?.prix || 19;
          prestationLabel = `Cours hebdomadaires - Propriétaire (tarif préférentiel)`;
          break;
        case 'cours_particulier':
          const tarifPart = prestations.find(p => p.category === 'cours_particuliers');
          prix = tarifPart?.prix || 30;
          prestationLabel = `Cours particuliers - ${selectedItems.length > 1 ? 'Séances' : 'Séance'}`;
          break;
        case 'cours_ponctuel':
          const tarifPonc = prestations.find(p => p.category === 'cours_ponctuels');
          prix = tarifPonc?.prix || 35;
          prestationLabel = `Cours ponctuels`;
          break;
        case 'stages':
          prix = 0; // Will need manual pricing
          prestationLabel = `Stages`;
          break;
        case 'baptemes':
          const tarifBapteme = prestations.find(p => p.category === 'baptemes');
          prix = tarifBapteme?.prix || 13;
          prestationLabel = `Baptêmes poney`;
          break;
        case 'competitions':
          const tarifComp = prestations.find(p => p.category === 'competitions');
          prix = tarifComp?.prix || 0;
          prestationLabel = `Compétitions`;
          break;
        default:
          prestationLabel = categoryData.label;
      }
      
      // Create a single line with quantity = number of selected items
      const dates = selectedItems.map(i => i.date?.slice(5, 10).replace('-', '/')).join(', ');
      newLignes.push({
        prestation: `${prestationLabel} (${dates})`,
        quantite: selectedItems.length,
        prix_unitaire: prix,
        total_ligne: selectedItems.length * prix,
        cours_ids: selectedIds,
        event_type: category
      });
    });
    
    if (newLignes.length > 0) {
      // Replace default empty line or add to existing lines
      const existingLignes = newFacture.lignes.filter(l => l.prestation || l.prix_unitaire > 0);
      setNewFacture({
        ...newFacture,
        lignes: [...existingLignes, ...newLignes]
      });
      
      // Clear selection after generating
      setSelectedPrestations({});
      
      toast.success(`${newLignes.length} ligne(s) de prestation ajoutée(s)`);
    }
  };

  // Fetch uninvoiced prestations when user is selected
  const fetchPrestationsNonFacturees = async (cavalierId) => {
    if (!cavalierId) {
      setPrestationsNonFacturees(null);
      setSelectedPrestations({});
      return;
    }
    
    setLoadingPrestationsNonFacturees(true);
    try {
      const response = await axios.get(`${API_URL}/api/factures/prestations-non-facturees/${cavalierId}`);
      setPrestationsNonFacturees(response.data);
    } catch (error) {
      console.error('Error fetching prestations:', error);
      setPrestationsNonFacturees(null);
    } finally {
      setLoadingPrestationsNonFacturees(false);
    }
  };

  // Fetch cours facturation status for manual management
  const fetchCoursFacturation = async (cavalierId) => {
    if (!cavalierId) return;
    setLoadingCoursFacturation(true);
    try {
      const response = await axios.get(`${API_URL}/api/factures/cours-facturation/${cavalierId}`);
      setCoursFacturationData(response.data);
    } catch (error) {
      console.error('Error fetching cours facturation:', error);
      toast.error('Erreur lors du chargement des cours');
    } finally {
      setLoadingCoursFacturation(false);
    }
  };

  // Toggle manual billing mark for a course
  const toggleCoursFacturation = async (coursId, currentStatus) => {
    if (!selectedCavalierForBilling) return;
    try {
      await axios.post(
        `${API_URL}/api/factures/cours-facturation/${selectedCavalierForBilling}/${coursId}?marque_facture=${!currentStatus}`
      );
      // Refresh data
      await fetchCoursFacturation(selectedCavalierForBilling);
      // Also refresh prestations if dialog is open
      if (newFacture.cavalier_id === selectedCavalierForBilling) {
        await fetchPrestationsNonFacturees(selectedCavalierForBilling);
      }
      toast.success(currentStatus ? 'Cours marqué comme non facturé' : 'Cours marqué comme facturé');
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  // Remove manual mark (revert to automatic)
  const removeCoursFacturationMark = async (coursId) => {
    if (!selectedCavalierForBilling) return;
    try {
      await axios.delete(`${API_URL}/api/factures/cours-facturation/${selectedCavalierForBilling}/${coursId}`);
      await fetchCoursFacturation(selectedCavalierForBilling);
      if (newFacture.cavalier_id === selectedCavalierForBilling) {
        await fetchPrestationsNonFacturees(selectedCavalierForBilling);
      }
      toast.success('Marquage manuel supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Open billing management dialog
  const openCoursFacturationDialog = (cavalierId) => {
    setSelectedCavalierForBilling(cavalierId);
    setShowCoursFacturationDialog(true);
    fetchCoursFacturation(cavalierId);
  };

  useEffect(() => {
    fetchData();
    fetchBaremes();
    if (isInstructor()) {
      fetchPrestationsBareme();
    }
  }, [statusFilter]);
  
  // Sync tab with URL param and default setting
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['tarifs', 'factures', 'decompte'].includes(tabParam)) {
      setMainTab(tabParam);
    }
  }, [searchParams]);
  
  // Fetch default billing view from club settings
  useEffect(() => {
    const fetchDefaultView = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/club`);
        const defaultView = response.data?.default_billing_view;
        const facturationModule = response.data?.facturation_enabled || false;
        
        setDefaultBillingView(defaultView);
        setFacturationEnabled(facturationModule);
        
        // Apply default view only if no tab param in URL
        if (!searchParams.get('tab')) {
          if (defaultView === 'decompte') {
            setMainTab('decompte');
          } else if (defaultView === 'tarifs') {
            setMainTab('tarifs');
          } else if (defaultView === 'facturation' && facturationModule) {
            setMainTab('factures');
          } else {
            // Default to decompte if facturation is disabled or no preference
            setMainTab('decompte');
          }
        } else {
          // If tab param is 'factures' but facturation is disabled, redirect to decompte
          const tabParam = searchParams.get('tab');
          if (tabParam === 'factures' && !facturationModule) {
            setMainTab('decompte');
          }
        }
      } catch (error) {
        console.error('Error fetching club settings:', error);
      }
    };
    fetchDefaultView();
  }, []);
  
  const handleMainTabChange = (value) => {
    setMainTab(value);
    setSearchParams({ tab: value });
  };
  
  // ============ BAREMES FUNCTIONS ============
  
  const fetchBaremes = async () => {
    try {
      const [baremesRes, currentRes] = await Promise.all([
        axios.get(`${API_URL}/api/baremes`),
        axios.get(`${API_URL}/api/baremes/current`).catch(() => ({ data: null }))
      ]);
      setBaremes(baremesRes.data);
      setCurrentBareme(currentRes.data);
    } catch (error) {
      console.error('Error fetching baremes:', error);
    } finally {
      setLoadingBaremes(false);
    }
  };
  
  const handleCopyFromPrevious = () => {
    if (baremes.length > 0) {
      const previousBareme = currentBareme || baremes[0];
      if (previousBareme?.tarifs && Object.keys(previousBareme.tarifs).length > 0) {
        const copiedTarifs = JSON.parse(JSON.stringify(previousBareme.tarifs));
        if (copiedTarifs.forfaits_t3) {
          delete copiedTarifs.forfaits_t3;
        }
        setNewBareme(prev => ({
          ...prev,
          tarifs: copiedTarifs
        }));
        setCopyFromPrevious(true);
        toast.success('Tarifs copiés depuis le barème précédent');
      } else {
        toast.info('Aucun tarif à copier, utilisation des valeurs par défaut');
      }
    }
  };

  const handleCreateBareme = async () => {
    if (!newBareme.saison || !newBareme.date_debut || !newBareme.date_fin) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    try {
      const tarifsToSave = { ...newBareme.tarifs };
      if (tarifsToSave.forfaits_t3) {
        delete tarifsToSave.forfaits_t3;
      }
      
      await axios.post(`${API_URL}/api/baremes`, {
        ...newBareme,
        tarifs: tarifsToSave,
        statut: 'brouillon'
      });
      toast.success('Barème créé en brouillon');
      setIsBaremeDialogOpen(false);
      resetBaremeForm();
      fetchBaremes();
    } catch (error) {
      toast.error('Erreur lors de la création du barème');
    }
  };

  const handleUpdateBareme = async () => {
    if (!editingBareme) return;
    
    try {
      const tarifsToSave = { ...editingBareme.tarifs };
      if (tarifsToSave.forfaits_t3) {
        delete tarifsToSave.forfaits_t3;
      }
      
      await axios.put(`${API_URL}/api/baremes/${editingBareme.id}`, {
        saison: editingBareme.saison,
        date_debut: editingBareme.date_debut,
        date_fin: editingBareme.date_fin,
        statut: editingBareme.statut,
        tarifs: tarifsToSave
      });
      toast.success('Barème mis à jour');
      setIsBaremeEditMode(false);
      setEditingBareme(null);
      fetchBaremes();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du barème');
    }
  };

  const handleActivateBareme = async (id) => {
    try {
      await axios.put(`${API_URL}/api/baremes/${id}`, {
        ...baremes.find(b => b.id === id),
        statut: 'actif'
      });
      toast.success('Barème activé');
      fetchBaremes();
    } catch (error) {
      toast.error('Erreur lors de l\'activation');
    }
  };

  const handleDeleteBareme = async (id) => {
    if (!window.confirm('Supprimer ce barème ?')) return;
    try {
      await axios.delete(`${API_URL}/api/baremes/${id}`);
      toast.success('Barème supprimé');
      fetchBaremes();
    } catch (error) {
      toast.error('Impossible de supprimer ce barème');
    }
  };

  const resetBaremeForm = () => {
    setNewBareme({
      saison: '',
      date_debut: '',
      date_fin: '',
      statut: 'brouillon',
      tarifs: JSON.parse(JSON.stringify(defaultTarifs))
    });
    setCopyFromPrevious(false);
  };

  const handleBaremeTarifChange = (categoryKey, itemKey, value, isEditing = false) => {
    const parsedValue = parseFloat(value) || 0;
    
    if (isEditing && editingBareme) {
      setEditingBareme(prev => ({
        ...prev,
        tarifs: {
          ...prev.tarifs,
          [categoryKey]: {
            ...prev.tarifs[categoryKey],
            items: {
              ...prev.tarifs[categoryKey].items,
              [itemKey]: {
                ...prev.tarifs[categoryKey].items[itemKey],
                prix: parsedValue
              }
            }
          }
        }
      }));
    } else {
      setNewBareme(prev => ({
        ...prev,
        tarifs: {
          ...prev.tarifs,
          [categoryKey]: {
            ...prev.tarifs[categoryKey],
            items: {
              ...prev.tarifs[categoryKey].items,
              [itemKey]: {
                ...prev.tarifs[categoryKey].items[itemKey],
                prix: parsedValue
              }
            }
          }
        }
      }));
    }
  };
  
  const fetchPrestationsBareme = async () => {
    setLoadingPrestations(true);
    try {
      const response = await axios.get(`${API_URL}/api/factures/prestations`);
      setPrestations(response.data.prestations || []);
    } catch (error) {
      console.error('Error fetching prestations:', error);
    } finally {
      setLoadingPrestations(false);
    }
  };

  const fetchData = async () => {
    try {
      const params = statusFilter !== 'all' ? { statut: statusFilter } : {};
      const [facturesRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/factures`, { params }),
        isInstructor() ? axios.get(`${API_URL}/api/users`) : Promise.resolve({ data: [] })
      ]);
      setFactures(facturesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error fetching billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddLigne = () => {
    setNewFacture({
      ...newFacture,
      lignes: [...newFacture.lignes, { prestation: '', quantite: 1, prix_unitaire: 0, total_ligne: 0 }]
    });
  };

  // Helper to extract horse names from invoice lines (for pension/demi-pension)
  const getHorseNamesFromFacture = (facture) => {
    if (!facture.lignes || facture.lignes.length === 0) return '';
    if (!['pension_mensuelle', 'demi_pension_mensuelle'].includes(facture.type_facture)) return '';
    
    const horseNames = facture.lignes
      .map(ligne => {
        // Extract horse name from prestation like "Pension MAYOR - Box cheval paille"
        const match = ligne.prestation?.match(/^(?:Pension|Demi-pension)\s+([A-ZÀÂÄÉÈÊËÏÎÔÙÛÜ][A-Za-zàâäéèêëïîôùûüç\s-]*?)(?:\s+-|$)/i);
        return match ? match[1].trim() : null;
      })
      .filter(Boolean);
    
    return horseNames.length > 0 ? ` (${horseNames.join(', ')})` : '';
  };

  const handleUpdateLigne = (index, field, value) => {
    const lignes = [...newFacture.lignes];
    lignes[index][field] = value;
    
    if (field === 'quantite' || field === 'prix_unitaire') {
      lignes[index].total_ligne = lignes[index].quantite * lignes[index].prix_unitaire;
    }
    
    setNewFacture({ ...newFacture, lignes });
  };

  const handleRemoveLigne = (index) => {
    const lignes = newFacture.lignes.filter((_, i) => i !== index);
    setNewFacture({ ...newFacture, lignes });
  };
  
  // Select a prestation from barème and auto-fill price
  const handleSelectPrestation = (index, prestationId) => {
    const prestation = prestations.find(p => p.id === prestationId);
    if (prestation) {
      const lignes = [...newFacture.lignes];
      // Include category label in the prestation name
      const catLabel = categoryLabels[prestation.category]?.replace(/^[^\s]+\s/, '') || prestation.category_label || prestation.category;
      const fullLabel = `${catLabel} - ${prestation.label}`;
      lignes[index] = {
        ...lignes[index],
        prestation: fullLabel,
        prix_unitaire: prestation.prix,
        total_ligne: lignes[index].quantite * prestation.prix
      };
      setNewFacture({ ...newFacture, lignes });
    }
  };
  
  // Open edit dialog for draft invoice
  const handleEditFacture = (facture) => {
    setEditingFacture({
      ...facture,
      lignes: facture.lignes?.map(l => ({
        prestation: l.prestation || l.description || '',
        quantite: l.quantite || 1,
        prix_unitaire: l.prix_unitaire || 0,
        total_ligne: l.total_ligne || 0
      })) || []
    });
  };
  
  // Update editing facture ligne
  const handleUpdateEditLigne = (index, field, value) => {
    const lignes = [...editingFacture.lignes];
    lignes[index][field] = value;
    
    if (field === 'quantite' || field === 'prix_unitaire') {
      lignes[index].total_ligne = lignes[index].quantite * lignes[index].prix_unitaire;
    }
    
    setEditingFacture({ ...editingFacture, lignes });
  };
  
  // Add ligne to editing facture
  const handleAddEditLigne = () => {
    setEditingFacture({
      ...editingFacture,
      lignes: [...editingFacture.lignes, { prestation: '', quantite: 1, prix_unitaire: 0, total_ligne: 0 }]
    });
  };
  
  // Remove ligne from editing facture
  const handleRemoveEditLigne = (index) => {
    const lignes = editingFacture.lignes.filter((_, i) => i !== index);
    setEditingFacture({ ...editingFacture, lignes });
  };
  
  // Select prestation for editing facture
  const handleSelectEditPrestation = (index, prestationId) => {
    const prestation = prestations.find(p => p.id === prestationId);
    if (prestation) {
      const lignes = [...editingFacture.lignes];
      // Include category label in the prestation name
      const catLabel = categoryLabels[prestation.category]?.replace(/^[^\s]+\s/, '') || prestation.category_label || prestation.category;
      const fullLabel = `${catLabel} - ${prestation.label}`;
      lignes[index] = {
        ...lignes[index],
        prestation: fullLabel,
        prix_unitaire: prestation.prix,
        total_ligne: lignes[index].quantite * prestation.prix
      };
      setEditingFacture({ ...editingFacture, lignes });
    }
  };
  
  // Get total for editing facture
  const getEditTotal = () => {
    return editingFacture?.lignes?.reduce((sum, ligne) => sum + (ligne.total_ligne || 0), 0) || 0;
  };
  
  // Save edited facture
  const handleSaveEditFacture = async () => {
    if (!editingFacture || editingFacture.lignes.length === 0) {
      toast.error('Veuillez ajouter au moins une ligne');
      return;
    }
    
    setIsSavingEdit(true);
    try {
      await axios.put(`${API_URL}/api/factures/${editingFacture.id}`, {
        cavalier_id: editingFacture.cavalier_id,
        type_facture: editingFacture.type_facture,
        date_emission: editingFacture.date_emission,
        date_echeance: editingFacture.date_echeance,
        lignes: editingFacture.lignes,
        notes: editingFacture.notes || '',
        mois_concerne: editingFacture.mois_concerne
      });
      toast.success('Facture mise à jour');
      setEditingFacture(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la mise à jour'));
    } finally {
      setIsSavingEdit(false);
    }
  };
  
  // Toggle selection for merge
  const toggleMergeSelection = (factureId) => {
    setSelectedForMerge(prev => {
      if (prev.includes(factureId)) {
        return prev.filter(id => id !== factureId);
      } else {
        return [...prev, factureId];
      }
    });
  };
  
  // Get invoices eligible for merge (same cavalier, draft status)
  const getMergeableInvoices = () => {
    if (selectedForMerge.length === 0) return factures.filter(f => f.statut === 'brouillon');
    
    const firstSelected = factures.find(f => f.id === selectedForMerge[0]);
    if (!firstSelected) return [];
    
    return factures.filter(f => 
      f.statut === 'brouillon' && 
      f.cavalier_id === firstSelected.cavalier_id
    );
  };
  
  // Merge selected invoices
  const handleMergeFactures = async () => {
    if (selectedForMerge.length < 2) {
      toast.error('Sélectionnez au moins 2 factures à fusionner');
      return;
    }
    
    const selectedFactures = factures.filter(f => selectedForMerge.includes(f.id));
    const cavalierIds = [...new Set(selectedFactures.map(f => f.cavalier_id))];
    
    if (cavalierIds.length > 1) {
      toast.error('Les factures doivent appartenir au même cavalier');
      return;
    }
    
    setIsMerging(true);
    try {
      const response = await axios.post(`${API_URL}/api/factures/merge`, {
        facture_ids: selectedForMerge
      });
      toast.success(`Factures fusionnées en ${response.data.facture?.numero_facture || 'nouvelle facture'}`);
      setMergeMode(false);
      setSelectedForMerge([]);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la fusion'));
    } finally {
      setIsMerging(false);
    }
  };

  const handleCreateFacture = async () => {
    if (!newFacture.cavalier_id || newFacture.lignes.length === 0) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/factures`, newFacture);
      toast.success('Facture créée avec succès');
      setIsDialogOpen(false);
      setNewFacture({
        cavalier_id: '',
        type_facture: 'cours',
        date_emission: new Date().toISOString().split('T')[0],
        date_echeance: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        lignes: [{ prestation: '', quantite: 1, prix_unitaire: 0, total_ligne: 0 }],
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création de la facture');
    }
  };

  // Fetch billing preview
  const handleFetchApercu = async () => {
    if (!generationType) {
      toast.error('Veuillez sélectionner un type de facturation');
      return;
    }
    
    setIsLoadingApercu(true);
    try {
      const params = {
        type_generation: generationType,
        annee: generationAnnee
      };
      
      if (['pensions_mensuelles', 'demi_pensions'].includes(generationType)) {
        params.mois = generationMois;
      } else {
        params.trimestre = generationTrimestre;
      }
      
      const response = await axios.get(`${API_URL}/api/facturation/apercu`, { params });
      setApercu(response.data);
    } catch (error) {
      console.error('Error fetching apercu:', error);
      toast.error(getErrorMessage(error, 'Erreur lors du chargement de l\'aperçu'));
      setApercu(null);
    } finally {
      setIsLoadingApercu(false);
    }
  };

  // Generate invoices
  const handleGenerateFactures = async () => {
    if (!generationType) {
      toast.error('Veuillez sélectionner un type de facturation');
      return;
    }
    
    setIsGenerating(true);
    try {
      const payload = {
        type_generation: generationType,
        annee: generationAnnee
      };
      
      if (['pensions_mensuelles', 'demi_pensions'].includes(generationType)) {
        payload.mois = generationMois;
      } else {
        payload.trimestre = generationTrimestre;
      }
      
      const response = await axios.post(`${API_URL}/api/facturation/generer`, payload);
      
      if (response.data.factures_generees > 0) {
        // Some invoices were generated
        toast.success(`${response.data.factures_generees} facture(s) générée(s) pour un total de ${response.data.montant_total.toFixed(2)} €`);
        if (response.data.erreurs && response.data.erreurs.length > 0) {
          // Also show info about skipped duplicates
          toast.info(`${response.data.erreurs.length} facture(s) ignorée(s) (déjà existantes)`);
        }
        setIsGenerationDialogOpen(false);
        setApercu(null);
        setGenerationType('');
        fetchData();
      } else if (response.data.erreurs && response.data.erreurs.length > 0) {
        // No invoices generated, all were duplicates
        toast.warning(`Aucune facture générée : ${response.data.erreurs.length} facture(s) existe(nt) déjà pour cette période`);
        setIsGenerationDialogOpen(false);
      } else {
        // No invoices and no errors - nothing to generate
        toast.info('Aucune facture à générer pour cette période');
        setIsGenerationDialogOpen(false);
      }
    } catch (error) {
      console.error('Error generating invoices:', error);
      const detail = error.response?.data?.detail;
      let errorMessage = 'Erreur lors de la génération des factures';
      if (detail) {
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
      }
      toast.error(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadPDF = async (factureId) => {
    try {
      const response = await axios.get(`${API_URL}/api/factures/${factureId}/pdf`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });
      
      // Check if response is actually a PDF
      if (response.data.type !== 'application/pdf') {
        // Try to read error message if it's not a PDF
        const text = await response.data.text();
        console.error('PDF Error:', text);
        toast.error('Erreur lors du téléchargement du PDF');
        return;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture_${factureId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF téléchargé');
    } catch (error) {
      console.error('PDF download error:', error);
      const message = getErrorMessage(error, 'Erreur lors du téléchargement');
      toast.error(message);
    }
  };

  const handleDeleteFacture = async (factureId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette facture ?')) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/factures/${factureId}`);
      toast.success('Facture supprimée');
      setSelectedFacture(null);
      fetchData();
    } catch (error) {
      const message = getErrorMessage(error, 'Erreur lors de la suppression');
      toast.error(message);
    }
  };

  // ========== Invoice Status Management ==========
  const handleEnvoyerFacture = async (factureId) => {
    try {
      await axios.post(`${API_URL}/api/factures/${factureId}/envoyer`);
      toast.success('Facture envoyée au cavalier');
      setSelectedFacture(null);
      fetchData();
    } catch (error) {
      const message = getErrorMessage(error, 'Erreur lors de l\'envoi');
      toast.error(message);
    }
  };

  const handleAnnulerFacture = async (factureId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette facture ?')) {
      return;
    }
    try {
      await axios.post(`${API_URL}/api/factures/${factureId}/annuler`);
      toast.success('Facture annulée');
      setSelectedFacture(null);
      fetchData();
    } catch (error) {
      const message = getErrorMessage(error, 'Erreur lors de l\'annulation');
      toast.error(message);
    }
  };

  // Open modal to create credit note (avoir) for a numbered invoice
  const handleCreerAvoir = (factureId) => {
    const facture = factures.find(f => f.id === factureId);
    if (!facture) return;
    
    setAvoirFacture(facture);
    setAvoirCreerBrouillon(false); // Par défaut, pas de création de brouillon
    setShowAvoirModal(true);
  };

  // Confirm and create the avoir
  const confirmCreerAvoir = async () => {
    if (!avoirFacture) return;
    
    setIsCreatingAvoir(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/factures/${avoirFacture.id}/creer-avoir?creer_brouillon=false`
      );
      toast.success(response.data.message);
      setShowAvoirModal(false);
      setAvoirFacture(null);
      setSelectedFacture(null);
      fetchData();
    } catch (error) {
      const message = getErrorMessage(error, 'Erreur lors de la création de l\'avoir');
      toast.error(message);
    } finally {
      setIsCreatingAvoir(false);
    }
  };

  const handleSolderAvoir = async (avoirId) => {
    const avoir = factures.find(f => f.id === avoirId);
    if (!avoir) return;
    
    const confirmMsg = `Solder l'avoir N° ${avoir.numero_facture} ?

Cela marquera l'avoir comme remboursé/clôturé.
Cette action est irréversible.

Continuer ?`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    try {
      // Use the same endpoint as payment but with a specific note for avoirs
      await axios.post(`${API_URL}/api/factures/${avoirId}/paiement`, {
        montant: Math.abs(avoir.total_ttc),
        mode_paiement: 'autre',
        reference: 'Avoir soldé',
        notes: 'Avoir clôturé - remboursement effectué ou annulation sans paiement initial'
      });
      toast.success('Avoir soldé avec succès');
      setSelectedFacture(null);
      fetchData();
    } catch (error) {
      const message = getErrorMessage(error, 'Erreur lors du solde de l\'avoir');
      toast.error(message);
    }
  };

  const handleMarquerEnRetard = async (factureId) => {
    try {
      await axios.post(`${API_URL}/api/factures/${factureId}/marquer-en-retard`);
      toast.success('Facture marquée en retard');
      setSelectedFacture(null);
      fetchData();
    } catch (error) {
      const message = getErrorMessage(error, 'Erreur');
      toast.error(message);
    }
  };

  const [showPaiementDialog, setShowPaiementDialog] = useState(false);
  const [paiementForm, setPaiementForm] = useState({
    montant: '',
    mode_paiement: 'cb',
    notes: ''
  });

  const handleEnregistrerPaiement = async () => {
    if (!selectedFacture || !paiementForm.montant) {
      toast.error('Veuillez renseigner le montant');
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/api/factures/${selectedFacture.id}/paiement`, {
        montant: parseFloat(paiementForm.montant),
        mode_paiement: paiementForm.mode_paiement,
        notes: paiementForm.notes || null
      });
      toast.success(`Paiement de ${paiementForm.montant}€ enregistré`);
      setShowPaiementDialog(false);
      setPaiementForm({ montant: '', mode_paiement: 'cb', notes: '' });
      setSelectedFacture(response.data.facture);
      fetchData();
    } catch (error) {
      const message = getErrorMessage(error, 'Erreur lors de l\'enregistrement');
      toast.error(message);
    }
  };

  const handleEnvoyerSelection = async () => {
    const brouillons = factures.filter(f => f.statut === 'brouillon');
    if (brouillons.length === 0) {
      toast.info('Aucune facture brouillon à envoyer');
      return;
    }
    if (!window.confirm(`Envoyer ${brouillons.length} facture(s) brouillon aux cavaliers ?`)) {
      return;
    }
    try {
      const ids = brouillons.map(f => f.id);
      const response = await axios.post(`${API_URL}/api/factures/envoyer-selection`, ids);
      toast.success(`${response.data.envoyees.length} facture(s) envoyée(s)`);
      if (response.data.erreurs.length > 0) {
        toast.warning(`${response.data.erreurs.length} erreur(s)`);
      }
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const getRestantAPayer = (facture) => {
    const paye = facture.montant_paye || 0;
    return Math.max(0, facture.total_ttc - paye);
  };

  const getTotal = () => {
    return newFacture.lignes.reduce((sum, ligne) => sum + (ligne.total_ligne || 0), 0);
  };

  const getUserName = (userId) => {
    const u = users.find(u => u.id === userId);
    return u ? `${u.prenom} ${u.nom}` : 'Cavalier';
  };

  // Get selected user's role for filtering prestations
  const getSelectedUserRole = () => {
    if (!newFacture.cavalier_id) return null;
    const selectedUser = users.find(u => u.id === newFacture.cavalier_id);
    return selectedUser?.role_club || null;
  };
  
  // Get prestations filtered by selected user's role
  const filteredPrestationsForUser = getFilteredPrestations(prestations, getSelectedUserRole());
  
  // Group filtered prestations by category for display
  const groupedPrestations = groupPrestationsByCategory(filteredPrestationsForUser);

  const filteredFactures = factures.filter(f => {
    const userName = getUserName(f.cavalier_id).toLowerCase();
    const matchesSearch = userName.includes(searchTerm.toLowerCase()) || 
           f.numero_facture?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Type filter (facture/avoir)
    const matchesType = typeFilter === 'all' || 
           (typeFilter === 'facture' && f.type_document !== 'avoir') ||
           (typeFilter === 'avoir' && f.type_document === 'avoir');
    
    return matchesSearch && matchesType;
  });

  // Calculate total unpaid = sum of remaining amounts for unpaid invoices
  const totalUnpaid = factures
    .filter(f => ['en_attente', 'en_retard', 'payee_partiellement'].includes(f.statut))
    .reduce((sum, f) => sum + (f.total_ttc - (f.montant_paye || 0)), 0);

  // ============ TARIF EDITOR COMPONENT ============
  const TarifEditor = ({ tarifs, onUpdate, isEditing, readOnly = false }) => {
    // Merge database tarifs with defaults to show all categories
    const mergedTarifs = JSON.parse(JSON.stringify(defaultTarifs));
    
    // If tarifs from database exist, merge them in
    if (tarifs && Object.keys(tarifs).length > 0) {
      Object.entries(tarifs).forEach(([categoryKey, categoryValue]) => {
        if (categoryKey === 'forfaits_t3') return; // Skip old T3
        
        if (mergedTarifs[categoryKey]) {
          // Category exists in defaults - merge items
          if (categoryValue && typeof categoryValue === 'object') {
            if (categoryValue.items) {
              Object.entries(categoryValue.items).forEach(([itemKey, itemValue]) => {
                if (mergedTarifs[categoryKey].items[itemKey]) {
                  mergedTarifs[categoryKey].items[itemKey].prix = itemValue.prix || 0;
                  if (itemValue.note) mergedTarifs[categoryKey].items[itemKey].note = itemValue.note;
                }
              });
            } else {
              // Legacy format
              Object.entries(categoryValue).forEach(([itemKey, value]) => {
                if (mergedTarifs[categoryKey].items[itemKey]) {
                  mergedTarifs[categoryKey].items[itemKey].prix = typeof value === 'number' ? value : (value?.prix || 0);
                }
              });
            }
          }
        }
      });
    }
    
    return (
      <div className="space-y-6 max-h-[50vh] overflow-y-auto pr-2">
        {Object.entries(mergedTarifs).map(([categoryKey, category]) => {
          if (!category || !category.items) return null;
          
          return (
            <div key={categoryKey} className="space-y-3">
              <div className="border-b pb-2">
                <h4 className="font-medium text-stone-700">{category.label || categoryKey}</h4>
                {category.description && (
                  <p className="text-xs text-blue-600 mt-1">{category.description}</p>
                )}
              </div>
              <div className="grid gap-2">
                {Object.entries(category.items).map(([itemKey, item]) => {
                  if (!item) return null;
                  
                  return (
                    <div key={itemKey} className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <Label className="text-sm text-stone-600">{item.label || itemKey}</Label>
                        {item.note && (
                          <p className="text-xs text-stone-400 italic">{item.note}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 w-28">
                        {readOnly ? (
                          <span className="text-sm font-medium text-[#2E4A3D]">{item.prix || 0} €</span>
                        ) : (
                          <>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.prix || 0}
                              onChange={(e) => onUpdate(categoryKey, itemKey, e.target.value, isEditing)}
                              className="w-20 h-8 text-right text-sm"
                            />
                            <span className="text-stone-500 text-sm">€</span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ============ COLOR CLASSES FOR TARIFS ============
  const colorClasses = {
    amber: "bg-amber-50 border-amber-200",
    blue: "bg-blue-50 border-blue-200",
    green: "bg-green-50 border-green-200",
    purple: "bg-purple-50 border-purple-200"
  };
  
  const badgeClasses = {
    amber: "bg-amber-100 text-amber-800",
    blue: "bg-blue-100 text-blue-800",
    green: "bg-green-100 text-green-800",
    purple: "bg-purple-100 text-purple-800"
  };

  if (loading && loadingBaremes) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn" data-testid="billing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-serif font-bold text-[#2E4A3D]">Tarifs et facturation</h1>
          <p className="text-stone-500 mt-1">
            {mainTab === 'factures' ? `${factures.length} factures` : `${baremes.length} barèmes tarifaires`}
          </p>
        </div>
      </div>
      
      {/* Main Tabs */}
      <Tabs value={mainTab} onValueChange={handleMainTabChange} className="w-full">
        <TabsList className={`grid w-full ${
          // Cavaliers: 2 onglets (Facturation OU Décompte + Tarifs)
          // Instructeurs/Admins: 2 ou 3 onglets selon facturation_enabled
          !isInstructor() ? 'grid-cols-2' : (facturationEnabled ? 'grid-cols-3' : 'grid-cols-2')
        } mb-6`}>
          {/* Cavaliers: Facturation ou Décompte selon le paramètre du club */}
          {!isInstructor() && (
            (defaultBillingView === 'facturation' && facturationEnabled) ? (
              <TabsTrigger value="factures" className="flex items-center gap-2">
                <Receipt className="w-4 h-4" />
                Mes factures
              </TabsTrigger>
            ) : (
              <TabsTrigger value="decompte" className="flex items-center gap-2">
                <ClipboardList className="w-4 h-4" />
                Mon activité
              </TabsTrigger>
            )
          )}
          {/* Instructeurs/Admins: Facturation only if enabled */}
          {isInstructor() && facturationEnabled && (
            <TabsTrigger value="factures" className="flex items-center gap-2">
              <Receipt className="w-4 h-4" />
              Facturation
            </TabsTrigger>
          )}
          {/* Instructeurs/Admins: Décompte always visible */}
          {isInstructor() && (
            <TabsTrigger value="decompte" className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4" />
              Décompte
            </TabsTrigger>
          )}
          {/* Tarifs always visible for everyone */}
          <TabsTrigger value="tarifs" className="flex items-center gap-2">
            <Euro className="w-4 h-4" />
            Tarifs
          </TabsTrigger>
        </TabsList>
        
        {/* TARIFS TAB CONTENT */}
        <TabsContent value="tarifs" className="space-y-6">
          {!isAdmin() ? (
            // Vue cavalier : tarifs en lecture seule
            <>
              {currentBareme ? (
                <>
                  <Card className="border-2 border-green-500">
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <Euro className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">Saison {currentBareme.saison}</CardTitle>
                          <p className="text-sm text-stone-500 flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            Du {new Date(currentBareme.date_debut).toLocaleDateString('fr-FR')} au {new Date(currentBareme.date_fin).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Euro className="w-5 h-5 text-[#2E4A3D]" />
                        Grille tarifaire
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {currentBareme.tarifs && Object.keys(currentBareme.tarifs).length > 0 ? (
                        <TarifEditor 
                          tarifs={currentBareme.tarifs} 
                          onUpdate={() => {}}
                          isEditing={false}
                          readOnly={true}
                        />
                      ) : (
                        <p className="text-stone-500 text-sm">Tarifs non disponibles pour ce barème</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Règles tarifaires par profil - Composant dynamique */}
                  <PricingRulesTable />
                </>
              ) : (
                <Card className="p-8 text-center">
                  <Info className="w-12 h-12 mx-auto text-stone-400 mb-4" />
                  <p className="text-stone-600">Aucun barème actif pour le moment.</p>
                </Card>
              )}
            </>
          ) : (
            // Vue admin : gestion des barèmes
            <>
              <div className="flex justify-end mb-4">
                <Dialog open={isBaremeDialogOpen} onOpenChange={(open) => { setIsBaremeDialogOpen(open); if (!open) resetBaremeForm(); }}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#2E4A3D] hover:bg-[#3d5f4f]">
                      <Plus className="w-4 h-4 mr-2" />
                      Nouveau barème
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="font-serif">Créer un barème tarifaire</DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-6 mt-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <Label className="form-label">Saison *</Label>
                          <Input
                            placeholder="ex: 2024-2025"
                            value={newBareme.saison}
                            onChange={(e) => setNewBareme(prev => ({ ...prev, saison: e.target.value }))}
                            className="input-field"
                          />
                        </div>
                        <div>
                          <Label className="form-label">Date début *</Label>
                          <Input
                            type="date"
                            value={newBareme.date_debut}
                            onChange={(e) => setNewBareme(prev => ({ ...prev, date_debut: e.target.value }))}
                            className="input-field"
                          />
                        </div>
                        <div>
                          <Label className="form-label">Date fin *</Label>
                          <Input
                            type="date"
                            value={newBareme.date_fin}
                            onChange={(e) => setNewBareme(prev => ({ ...prev, date_fin: e.target.value }))}
                            className="input-field"
                          />
                        </div>
                      </div>
                      
                      {baremes.length > 0 && !copyFromPrevious && (
                        <Button 
                          variant="outline" 
                          onClick={handleCopyFromPrevious}
                          className="w-full"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copier les tarifs du barème précédent
                        </Button>
                      )}
                      
                      <div>
                        <Label className="form-label mb-3 block">Tarifs</Label>
                        <TarifEditor
                          tarifs={newBareme.tarifs}
                          onUpdate={handleBaremeTarifChange}
                          isEditing={false}
                        />
                      </div>
                      
                      <Button onClick={handleCreateBareme} className="w-full bg-[#2E4A3D] hover:bg-[#3d5f4f]">
                        Créer en brouillon
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              
              {/* Liste des barèmes */}
              <div className="space-y-4">
                {baremes.length === 0 ? (
                  <Card className="p-8 text-center">
                    <Euro className="w-12 h-12 mx-auto text-stone-400 mb-4" />
                    <p className="text-stone-600">Aucun barème créé.</p>
                    <p className="text-sm text-stone-400">Cliquez sur "Nouveau barème" pour commencer.</p>
                  </Card>
                ) : (
                  baremes.map(bareme => (
                    <Card key={bareme.id} className={`${bareme.statut === 'actif' ? 'border-2 border-green-500' : ''}`}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <CardTitle className="text-lg">Saison {bareme.saison}</CardTitle>
                            <Badge className={bareme.statut === 'actif' ? 'bg-green-100 text-green-800' : 'bg-stone-100 text-stone-800'}>
                              {bareme.statut === 'actif' ? 'Actif' : 'Brouillon'}
                            </Badge>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => { setEditingBareme({...bareme}); setIsBaremeEditMode(true); }}
                              title="Modifier le barème"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            {bareme.statut !== 'actif' && (
                              <>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-green-600 hover:text-green-700"
                                  onClick={() => handleActivateBareme(bareme.id)}
                                  title="Activer ce barème"
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => handleDeleteBareme(bareme.id)}
                                  title="Supprimer ce barème"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-stone-500">
                          Du {new Date(bareme.date_debut).toLocaleDateString('fr-FR')} au {new Date(bareme.date_fin).toLocaleDateString('fr-FR')}
                        </p>
                      </CardHeader>
                      <CardContent>
                        <TarifEditor 
                          tarifs={bareme.tarifs} 
                          onUpdate={() => {}}
                          isEditing={false}
                          readOnly={true}
                        />
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
              
              {/* Edit Bareme Dialog */}
              <Dialog open={isBaremeEditMode} onOpenChange={(open) => { setIsBaremeEditMode(open); if (!open) setEditingBareme(null); }}>
                <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-serif">Modifier le barème</DialogTitle>
                  </DialogHeader>
                  
                  {editingBareme && (
                    <div className="space-y-6 mt-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <Label className="form-label">Saison</Label>
                          <Input
                            value={editingBareme.saison}
                            onChange={(e) => setEditingBareme(prev => ({ ...prev, saison: e.target.value }))}
                            className="input-field"
                          />
                        </div>
                        <div>
                          <Label className="form-label">Date début</Label>
                          <Input
                            type="date"
                            value={editingBareme.date_debut?.slice(0, 10)}
                            onChange={(e) => setEditingBareme(prev => ({ ...prev, date_debut: e.target.value }))}
                            className="input-field"
                          />
                        </div>
                        <div>
                          <Label className="form-label">Date fin</Label>
                          <Input
                            type="date"
                            value={editingBareme.date_fin?.slice(0, 10)}
                            onChange={(e) => setEditingBareme(prev => ({ ...prev, date_fin: e.target.value }))}
                            className="input-field"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="form-label mb-3 block">Tarifs</Label>
                        <TarifEditor
                          tarifs={editingBareme.tarifs}
                          onUpdate={handleBaremeTarifChange}
                          isEditing={true}
                        />
                      </div>
                      
                      <Button onClick={handleUpdateBareme} className="w-full bg-[#2E4A3D] hover:bg-[#3d5f4f]">
                        Enregistrer les modifications
                      </Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
              
              {/* Règles tarifaires par profil - Visible également pour les admins */}
              <div className="mt-8">
                <PricingRulesTable />
              </div>
            </>
          )}
        </TabsContent>
        
        {/* FACTURES TAB CONTENT */}
        <TabsContent value="factures">
        {isInstructor() && (
          <div className="flex gap-2 mb-6 justify-end">
            {/* Automatic billing button - Admin only */}
            {isAdmin && isAdmin() && (
              <Dialog open={isGenerationDialogOpen} onOpenChange={(open) => { setIsGenerationDialogOpen(open); if (!open) { setApercu(null); setGenerationType(''); } }}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-[#2E4A3D] text-[#2E4A3D] hover:bg-[#2E4A3D]/10" data-testid="auto-billing-btn">
                    <Zap className="w-4 h-4 mr-2" />
                    Génération auto
                  </Button>
                </DialogTrigger>
                <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-serif flex items-center gap-2">
                      <Zap className="w-5 h-5 text-amber-500" />
                      Génération automatique de factures
                    </DialogTitle>
                  </DialogHeader>
                  
                  <div className="space-y-6 mt-4">
                    {/* Type selection */}
                    <div>
                      <Label className="form-label">Type de facturation</Label>
                      <div className="grid grid-cols-1 gap-2 mt-2">
                        {generationTypes.map(type => (
                          <button
                            key={type.value}
                            onClick={() => { setGenerationType(type.value); setApercu(null); }}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${
                              generationType === type.value 
                                ? 'border-[#2E4A3D] bg-[#2E4A3D]/5' 
                                : 'border-stone-200 hover:border-stone-300'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{type.icon}</span>
                              <div className="flex-1">
                                <div className="font-medium text-stone-800">{type.label}</div>
                                <div className="text-xs text-stone-500">{type.description}</div>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {type.timing}
                              </Badge>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Period selection */}
                    {generationType && (
                      <div className="p-4 bg-stone-50 rounded-lg space-y-4">
                        <Label className="form-label">Période concernée</Label>
                        
                        {['pensions_mensuelles', 'demi_pensions'].includes(generationType) ? (
                          <div>
                            <Label className="text-sm text-stone-600">Mois</Label>
                            <Input
                              type="month"
                              value={generationMois}
                              onChange={(e) => setGenerationMois(e.target.value)}
                              className="input-field"
                            />
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm text-stone-600">Trimestre</Label>
                              <Select value={generationTrimestre} onValueChange={setGenerationTrimestre}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="T4">
                                    T4 (Oct-Déc) {defaultTrimestreData.trimestre === 'T4' && '← Prochain'}
                                  </SelectItem>
                                  <SelectItem value="T1">
                                    T1 (Jan-Mars) {defaultTrimestreData.trimestre === 'T1' && '← Prochain'}
                                  </SelectItem>
                                  <SelectItem value="T2">
                                    T2 (Avril-Juin) {defaultTrimestreData.trimestre === 'T2' && '← Prochain'}
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-sm text-stone-600">Année de début de saison</Label>
                              <Input
                                type="number"
                                value={generationAnnee}
                                onChange={(e) => setGenerationAnnee(parseInt(e.target.value))}
                                className="input-field"
                              />
                              <p className="text-xs text-stone-500 mt-1">
                                Saison {generationAnnee}/{generationAnnee + 1}
                              </p>
                            </div>
                          </div>
                        )}
                        
                        <Button 
                          variant="outline" 
                          onClick={handleFetchApercu}
                          disabled={isLoadingApercu}
                          className="w-full"
                        >
                          {isLoadingApercu ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Chargement...
                            </>
                          ) : (
                            <>
                              <Eye className="w-4 h-4 mr-2" />
                              Voir l&apos;aperçu
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                    
                    {/* Preview */}
                    {apercu && (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-[#2E4A3D] text-white px-4 py-3 flex justify-between items-center">
                          <span className="font-medium">Aperçu des factures à générer</span>
                          <div className="text-right">
                            <div className="text-sm opacity-80">{apercu.total_factures} facture(s)</div>
                            <div className="font-bold">{apercu.total_montant_ttc?.toFixed(2)} € TTC</div>
                          </div>
                        </div>
                        
                        {apercu.apercu?.length > 0 ? (
                          <div className="max-h-[300px] overflow-y-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Cavalier</TableHead>
                                  <TableHead>Description</TableHead>
                                  <TableHead className="text-right">Montant TTC</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {apercu.apercu.map((item, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell className="font-medium">{item.cavalier_nom}</TableCell>
                                    <TableCell className="text-stone-600">{item.description}</TableCell>
                                    <TableCell className="text-right font-medium">{item.montant_ttc?.toFixed(2)} €</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <div className="p-8 text-center text-stone-500">
                            <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                            <p>Aucune facture à générer pour cette période</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Generate button */}
                    {apercu && apercu.total_factures > 0 && (
                      <Button
                        onClick={handleGenerateFactures}
                        disabled={isGenerating}
                        className="w-full btn-action"
                      >
                        {isGenerating ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Génération en cours...
                          </>
                        ) : (
                          <>
                            <Zap className="w-4 h-4 mr-2" />
                            Générer {apercu.total_factures} facture(s)
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            )}
            
            {/* Merge mode controls */}
            {mergeMode ? (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => { setMergeMode(false); setSelectedForMerge([]); }}
                >
                  Annuler
                </Button>
                <Button
                  className="btn-action"
                  onClick={handleMergeFactures}
                  disabled={selectedForMerge.length < 2 || isMerging}
                  data-testid="merge-confirm-btn"
                >
                  {isMerging ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Combine className="w-4 h-4 mr-2" />
                  )}
                  Fusionner ({selectedForMerge.length})
                </Button>
              </div>
            ) : (
              <>
                {/* Merge button - only show if there are drafts */}
                {factures.filter(f => f.statut === 'brouillon').length >= 2 && (
                  <Button
                    variant="outline"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                    onClick={() => setMergeMode(true)}
                    data-testid="merge-mode-btn"
                  >
                    <Combine className="w-4 h-4 mr-2" />
                    Fusionner
                  </Button>
                )}
            
            {/* Manual invoice creation */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-action" data-testid="new-invoice-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle facture
                </Button>
              </DialogTrigger>
              <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-serif">Créer une facture</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className="form-label">Utilisateur *</Label>
                    <Select 
                      value={newFacture.cavalier_id} 
                      onValueChange={(v) => {
                        setNewFacture({...newFacture, cavalier_id: v});
                        fetchPrestationsNonFacturees(v);
                      }}
                    >
                      <SelectTrigger data-testid="invoice-cavalier">
                        <SelectValue placeholder="Sélectionner un utilisateur" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.filter(u => u.statut === 'actif' && u.role_club && !u.role_club.startsWith('instructeur')).map(u => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.prenom} {u.nom} ({getRoleLabel(u.role_club)})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Prestations non facturées (hors forfait) */}
                  {loadingPrestationsNonFacturees && (
                    <div className="flex items-center gap-2 text-sm text-stone-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Chargement des prestations...
                    </div>
                  )}
                  
                  {prestationsNonFacturees && prestationsNonFacturees.has_prestations && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <CalendarDays className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-2">
                            <p className="font-semibold text-amber-800">
                              {prestationsNonFacturees.total_prestations} prestation(s) à facturer
                            </p>
                            <div className="flex gap-1">
                              <Button 
                                type="button" 
                                variant="outline" 
                                size="sm" 
                                className="text-xs h-7 px-2 bg-white text-blue-700 border-blue-300 hover:bg-blue-50"
                                onClick={() => openCoursFacturationDialog(newFacture.cavalier_id)}
                              >
                                <Settings2 className="w-3 h-3 mr-1" />
                                Gérer
                              </Button>
                              <Button 
                                type="button" 
                                variant="outline" 
                                size="sm" 
                                className="text-xs h-7 px-2 bg-white"
                                onClick={() => {
                                  // Select only items not already in invoice
                                  if (!prestationsNonFacturees?.prestations) return;
                                  const invoicedIds = getAlreadyInvoicedIds();
                                  const newSelection = {};
                                  Object.entries(prestationsNonFacturees.prestations).forEach(([cat, catData]) => {
                                    newSelection[cat] = catData.items
                                      .filter(item => !invoicedIds.has(item.id))
                                      .map(item => item.id);
                                  });
                                  setSelectedPrestations(newSelection);
                                }}
                              >
                                Tout sélectionner
                              </Button>
                              <Button 
                                type="button" 
                                variant="outline" 
                                size="sm" 
                                className="text-xs h-7 px-2 bg-white"
                                onClick={deselectAllPrestations}
                              >
                                Aucun
                              </Button>
                            </div>
                          </div>
                          
                          <div className="space-y-3 max-h-[250px] overflow-y-auto pr-1">
                            {Object.entries(prestationsNonFacturees.prestations).map(([category, data]) => {
                              const selectedInCategory = selectedPrestations[category] || [];
                              const invoicedIds = getAlreadyInvoicedIds();
                              const availableItems = data.items.filter(item => !invoicedIds.has(item.id));
                              const invoicedInCategory = data.items.filter(item => invoicedIds.has(item.id));
                              const allAvailableSelected = availableItems.length > 0 && 
                                availableItems.every(item => selectedInCategory.includes(item.id));
                              
                              return (
                                <div key={category} className="bg-white rounded-lg p-2 border border-amber-100">
                                  <div className="flex justify-between items-center mb-2">
                                    <div className="flex items-center gap-2">
                                      <Checkbox
                                        checked={allAvailableSelected}
                                        disabled={availableItems.length === 0}
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            // Select only available (non-invoiced) items
                                            setSelectedPrestations(prev => ({
                                              ...prev,
                                              [category]: availableItems.map(item => item.id)
                                            }));
                                          } else {
                                            deselectAllInCategory(category);
                                          }
                                        }}
                                        className="border-amber-400 data-[state=checked]:bg-amber-500 disabled:opacity-50"
                                      />
                                      <span className={`text-sm font-medium ${availableItems.length === 0 ? 'text-stone-400' : 'text-amber-900'}`}>
                                        {data.label}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      {invoicedInCategory.length > 0 && (
                                        <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs">
                                          {invoicedInCategory.length} ajouté(s)
                                        </span>
                                      )}
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                                        availableItems.length === 0 
                                          ? 'bg-stone-200 text-stone-500' 
                                          : 'bg-amber-200 text-amber-800'
                                      }`}>
                                        {selectedInCategory.length}/{availableItems.length}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  {data.items && data.items.length > 0 && (
                                    <div className="grid grid-cols-4 gap-1 ml-6">
                                      {data.items.map((item) => {
                                        const isInvoiced = invoicedIds.has(item.id);
                                        const isSelected = selectedInCategory.includes(item.id);
                                        return (
                                          <button
                                            key={item.id}
                                            type="button"
                                            disabled={isInvoiced}
                                            onClick={() => !isInvoiced && togglePrestationSelection(category, item.id)}
                                            className={`text-xs px-2 py-1 rounded transition-colors ${
                                              isInvoiced
                                                ? 'bg-green-100 text-green-600 cursor-not-allowed line-through'
                                                : isSelected 
                                                  ? 'bg-amber-500 text-white' 
                                                  : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                            }`}
                                            title={isInvoiced ? 'Déjà ajouté à la facture' : ''}
                                          >
                                            {item.date?.slice(8, 10)}/{item.date?.slice(5, 7)}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          
                          {/* Generate button */}
                          {getSelectedCount() > 0 && (
                            <div className="mt-3 pt-3 border-t border-amber-200">
                              <Button 
                                type="button"
                                onClick={generateLignesFromSelection}
                                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                              >
                                <Zap className="w-4 h-4 mr-2" />
                                Générer {getSelectedCount()} ligne(s) de facture
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {prestationsNonFacturees && !prestationsNonFacturees.has_prestations && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-amber-600" />
                          <div>
                            <p className="text-sm text-amber-700 font-medium">
                              Aucune prestation trouvée pour ce cavalier
                            </p>
                            <p className="text-xs text-amber-600">
                              Rôle: {prestationsNonFacturees.role || 'non défini'}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openCoursFacturationDialog(newFacture.cavalier_id)}
                            className="text-blue-700 border-blue-300 hover:bg-blue-100"
                          >
                            <Settings2 className="w-4 h-4 mr-1" />
                            Gérer
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              try {
                                const res = await axios.get(`${API_URL}/api/factures/debug-prestations/${newFacture.cavalier_id}`);
                                console.log('Debug prestations:', res.data);
                                alert(`DEBUG PRESTATIONS:\n\nCavalier: ${res.data.cavalier?.prenom} ${res.data.cavalier?.nom}\nRôle: ${res.data.role}\nFactures: ${res.data.factures_count}\nCours IDs déjà facturés: ${res.data.invoiced_course_ids?.length || 0}\nGroupes: ${res.data.groupes?.length || 0}\nCours trouvés (total): ${res.data.all_cours_count}\nCours passés: ${res.data.past_cours_count}\n\nDétails cours passés:\n${res.data.past_cours_details?.map(c => `- ${c.date} (${c.type_cours})`).join('\n') || 'Aucun'}`);
                              } catch (err) {
                                alert('Erreur: ' + err.message);
                              }
                            }}
                            className="text-amber-700 border-amber-300 hover:bg-amber-100"
                          >
                            <Search className="w-4 h-4 mr-1" />
                            Debug
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">Date d&apos;émission</Label>
                    <Input
                      type="date"
                      value={newFacture.date_emission}
                      onChange={(e) => setNewFacture({...newFacture, date_emission: e.target.value})}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Date d&apos;échéance</Label>
                    <Input
                      type="date"
                      value={newFacture.date_echeance}
                      onChange={(e) => setNewFacture({...newFacture, date_echeance: e.target.value})}
                      className="input-field"
                    />
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="form-label">Lignes de facturation</Label>
                    <Button variant="outline" size="sm" onClick={handleAddLigne}>
                      <Plus className="w-4 h-4 mr-1" /> Ajouter
                    </Button>
                  </div>
                  
                  {/* Helper text */}
                  <p className="text-xs text-stone-500 mb-3">
                    Sélectionnez une prestation du barème ou saisissez librement la description et le prix TTC.
                  </p>
                  
                  <div className="space-y-2">
                    {newFacture.lignes.map((ligne, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 items-end p-3 bg-stone-50 rounded-lg">
                        <div className="col-span-6">
                          <Label className="text-xs text-stone-500 mb-1 block">Prestation</Label>
                          <div className="flex gap-1">
                            <Input
                              placeholder="Description libre ou sélection →"
                              value={ligne.prestation}
                              onChange={(e) => handleUpdateLigne(index, 'prestation', e.target.value)}
                              className="input-field text-sm flex-1"
                              data-testid={`ligne-prestation-${index}`}
                            />
                            {filteredPrestationsForUser.length > 0 && newFacture.cavalier_id && (
                              <Select onValueChange={(v) => handleSelectPrestation(index, v)}>
                                <SelectTrigger className="w-10 h-9 p-0 justify-center border-amber-300 bg-amber-50 hover:bg-amber-100" title="Choisir depuis le barème">
                                  <span className="text-amber-700">▼</span>
                                </SelectTrigger>
                                <SelectContent className="max-h-[400px]">
                                  <div className="px-2 py-1.5 text-xs font-medium text-stone-500 border-b bg-stone-50 sticky top-0">
                                    Barème ({getRoleLabel(getSelectedUserRole())})
                                  </div>
                                  {groupedPrestations.map(group => (
                                    <div key={group.category}>
                                      <div className="px-2 py-1.5 text-xs font-semibold text-[#2E4A3D] bg-[#2E4A3D]/5 border-y border-stone-200 sticky top-8">
                                        {group.label}
                                      </div>
                                      {group.items.map(p => (
                                        <SelectItem key={p.id} value={p.id}>
                                          <span className="flex justify-between gap-2 w-full">
                                            <span>{p.label}</span>
                                            <span className="text-amber-600 font-bold">{p.prix}€</span>
                                          </span>
                                        </SelectItem>
                                      ))}
                                    </div>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        </div>
                        <div className="col-span-1">
                          <Label className="text-xs text-stone-500 mb-1 block">Qté</Label>
                          <Input
                            type="number"
                            min="1"
                            placeholder="1"
                            value={ligne.quantite}
                            onChange={(e) => handleUpdateLigne(index, 'quantite', parseInt(e.target.value) || 1)}
                            className="input-field text-sm text-center px-1"
                            data-testid={`ligne-quantite-${index}`}
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs text-stone-500 mb-1 block">Prix TTC</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={ligne.prix_unitaire}
                            onChange={(e) => handleUpdateLigne(index, 'prix_unitaire', parseFloat(e.target.value) || 0)}
                            className="input-field text-sm"
                            data-testid={`ligne-prix-${index}`}
                          />
                        </div>
                        <div className="col-span-2 text-right">
                          <Label className="text-xs text-stone-500 mb-1 block">Total</Label>
                          <p className="font-medium h-9 flex items-center justify-end">
                            {(ligne.total_ligne || 0).toFixed(2)}€
                          </p>
                        </div>
                        <div className="col-span-1 flex items-end justify-center">
                          {newFacture.lignes.length > 1 && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-500 h-9 w-9 p-0"
                              onClick={() => handleRemoveLigne(index)}
                              data-testid={`remove-ligne-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex justify-end mt-4 pt-4 border-t">
                    <div className="text-right">
                      <p className="text-sm text-stone-500">Total HT</p>
                      <p className="text-lg text-stone-600">{(getTotal() / 1.2).toFixed(2)}€</p>
                      <p className="text-sm text-stone-500 mt-1">TVA (20%)</p>
                      <p className="text-sm text-stone-600">{(getTotal() - getTotal() / 1.2).toFixed(2)}€</p>
                      <p className="text-sm font-semibold text-[#2E4A3D] mt-2">Total TTC</p>
                      <p className="text-2xl font-bold text-[#2E4A3D]">{getTotal().toFixed(2)}€</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <Label className="form-label">Notes</Label>
                  <Textarea
                    value={newFacture.notes}
                    onChange={(e) => setNewFacture({...newFacture, notes: e.target.value})}
                    placeholder="Notes ou commentaires..."
                    className="input-field min-h-[60px]"
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <Button 
                    className="btn-primary flex-1" 
                    onClick={handleCreateFacture}
                    data-testid="create-invoice-btn"
                  >
                    Créer la facture
                  </Button>
                  
                  {/* Info: Notifications are automatic */}
                  {newFacture.cavalier_id && (
                    <div className="flex items-center gap-1 text-xs text-stone-500" title="Le cavalier sera notifié automatiquement">
                      <Bell className="w-3 h-3" />
                      <span>Notif. auto</span>
                    </div>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
          </>
          )}
        </div>
        )}

      {/* Stats */}
      {isInstructor() && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-500">Total facturé</p>
                  <p className="text-xl font-bold text-[#2E4A3D]">
                    {factures.reduce((sum, f) => sum + f.total_ttc, 0).toFixed(0)}€
                  </p>
                </div>
                <div className="w-10 h-10 rounded-full bg-[#2E4A3D]/10 flex items-center justify-center">
                  <Euro className="w-5 h-5 text-[#2E4A3D]" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-500">En attente</p>
                  <p className="text-xl font-bold text-amber-600">{totalUnpaid.toFixed(0)}€</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-500">Payées</p>
                  <p className="text-xl font-bold text-green-600">
                    {factures.filter(f => f.statut === 'payee').length}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-500">En retard</p>
                  <p className="text-xl font-bold text-red-600">
                    {factures.filter(f => f.statut === 'en_retard').length}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
          <Input
            placeholder="Rechercher une facture..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 input-field"
            data-testid="search-invoices"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            {Object.entries(statusConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>
                {config.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-36">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous types</SelectItem>
            <SelectItem value="facture">Factures</SelectItem>
            <SelectItem value="avoir">Avoirs</SelectItem>
          </SelectContent>
        </Select>

        {/* Send all drafts button */}
        {isInstructor() && factures.filter(f => f.statut === 'brouillon').length > 0 && (
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={handleEnvoyerSelection}
            data-testid="send-all-drafts"
          >
            <Send className="w-4 h-4" />
            Envoyer tous les brouillons ({factures.filter(f => f.statut === 'brouillon').length})
          </Button>
        )}
      </div>

      {/* Invoices list */}
      <Card>
        <CardContent className="p-0">
          {filteredFactures.length === 0 ? (
            <div className="empty-state py-12">
              <Receipt className="empty-state-icon" />
              <h3 className="empty-state-title">Aucune facture</h3>
              <p className="empty-state-description">Les factures apparaîtront ici</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {mergeMode && <TableHead className="w-10"></TableHead>}
                    <TableHead>N° Facture</TableHead>
                    {isInstructor() && <TableHead>Cavalier</TableHead>}
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Montant TTC</TableHead>
                    {isInstructor() && <TableHead className="text-right">Payé / Reste</TableHead>}
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredFactures.map((facture) => {
                    const status = statusConfig[facture.statut] || statusConfig.brouillon;
                    const StatusIcon = status.icon;
                    const restant = getRestantAPayer(facture);
                    const paye = facture.montant_paye || 0;
                    const mergeableInvoices = getMergeableInvoices();
                    const canMerge = mergeMode && mergeableInvoices.some(f => f.id === facture.id);
                    
                    return (
                      <TableRow 
                        key={facture.id} 
                        data-testid={`invoice-row-${facture.id}`}
                        className={mergeMode && !canMerge ? 'opacity-40' : ''}
                      >
                        {mergeMode && (
                          <TableCell className="w-10">
                            {canMerge && (
                              <Checkbox
                                checked={selectedForMerge.includes(facture.id)}
                                onCheckedChange={() => toggleMergeSelection(facture.id)}
                                data-testid={`merge-checkbox-${facture.id}`}
                              />
                            )}
                          </TableCell>
                        )}
                        <TableCell className="font-medium">
                          <div className="flex items-start gap-2">
                            {facture.type_document === 'avoir' && (
                              <Badge className="bg-red-100 text-red-700 text-xs mt-0.5">AVOIR</Badge>
                            )}
                            <div>
                              {facture.numero_facture || <span className="text-gray-400 italic">Brouillon</span>}
                              {facture.type_document === 'avoir' && facture.facture_source_numero && (
                                <div className="text-xs text-gray-500 mt-0.5">
                                  → Réf: {facture.facture_source_numero}
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        {isInstructor() && (
                          <TableCell>{getUserName(facture.cavalier_id)}</TableCell>
                        )}
                        <TableCell>
                          {format(new Date(facture.date_emission), 'dd/MM/yyyy')}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          <div>
                            {facture.total_ttc?.toFixed(2)}€
                            {/* Afficher le solde pour les avoirs */}
                            {facture.type_document === 'avoir' && (
                              <div className="text-xs mt-0.5">
                                {facture.montant_paye_origine > 0 ? (
                                  <span className="text-orange-600">
                                    Remb: {facture.montant_paye_origine?.toFixed(2)}€
                                  </span>
                                ) : (
                                  <span className="text-emerald-600">Pas de remb.</span>
                                )}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        {isInstructor() && (
                          <TableCell className="text-right text-sm">
                            {facture.statut === 'brouillon' || facture.statut === 'annulee' ? (
                              <span className="text-stone-400">-</span>
                            ) : (
                              <>
                                {paye > 0 && (
                                  <span className="text-green-600">{paye.toFixed(2)}€</span>
                                )}
                                {paye > 0 && restant > 0 && ' / '}
                                {restant > 0 && (
                                  <span className="text-orange-600">{restant.toFixed(2)}€</span>
                                )}
                                {paye > 0 && restant === 0 && (
                                  <span className="text-green-600 ml-1">✓</span>
                                )}
                              </>
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          <Badge className={status.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedFacture(facture)}
                              data-testid={`view-invoice-${facture.id}`}
                              title="Voir détails"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            
                            {/* Send draft */}
                            {isInstructor() && facture.statut === 'brouillon' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditFacture(facture)}
                                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                  data-testid={`edit-invoice-${facture.id}`}
                                  title="Modifier"
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEnvoyerFacture(facture.id)}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  data-testid={`send-invoice-${facture.id}`}
                                  title="Envoyer au cavalier"
                                >
                                  <Send className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            
                            {/* Record payment */}
                            {isInstructor() && ['en_attente', 'payee_partiellement', 'en_retard'].includes(facture.statut) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setSelectedFacture(facture); setShowPaiementDialog(true); }}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                data-testid={`payment-invoice-${facture.id}`}
                                title="Enregistrer paiement"
                              >
                                <CreditCard className="w-4 h-4" />
                              </Button>
                            )}
                            
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadPDF(facture.id)}
                              data-testid={`download-invoice-${facture.id}`}
                              title="Télécharger PDF"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            
                            {/* Delete (only draft/brouillon) */}
                            {isInstructor() && facture.statut === 'brouillon' && !facture.numero_facture && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteFacture(facture.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-invoice-${facture.id}`}
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Dialog */}
      <Dialog open={showPaiementDialog} onOpenChange={setShowPaiementDialog}>
        <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enregistrer un paiement</DialogTitle>
          </DialogHeader>
          {selectedFacture && (
            <div className="space-y-4">
              {/* Invoice summary */}
              <div className="bg-gradient-to-r from-stone-50 to-stone-100 p-4 rounded-lg border">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-semibold text-[#2E4A3D]">{selectedFacture.numero_facture}</p>
                    <p className="text-sm text-stone-500">{getUserName(selectedFacture.cavalier_id)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-[#2E4A3D]">{selectedFacture.total_ttc?.toFixed(2)}€</p>
                    <p className="text-xs text-stone-500">Total TTC</p>
                  </div>
                </div>
                
                {/* Progress bar */}
                <div className="mb-2">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-green-600 font-medium">Payé: {(selectedFacture.montant_paye || 0).toFixed(2)}€</span>
                    <span className="text-orange-600 font-medium">Reste: {getRestantAPayer(selectedFacture).toFixed(2)}€</span>
                  </div>
                  <div className="w-full bg-stone-200 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all" 
                      style={{ width: `${Math.min(100, ((selectedFacture.montant_paye || 0) / selectedFacture.total_ttc) * 100)}%` }}
                    />
                  </div>
                </div>
                
                {/* Payment history */}
                {selectedFacture.paiements && selectedFacture.paiements.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-stone-200">
                    <p className="text-xs font-medium text-stone-500 mb-2">Historique des paiements</p>
                    <div className="space-y-1 max-h-20 overflow-y-auto">
                      {selectedFacture.paiements.map((p, idx) => (
                        <div key={idx} className="flex justify-between text-xs bg-white/50 px-2 py-1 rounded">
                          <span>{format(new Date(p.date), 'dd/MM/yy')} - {paymentMethods.find(m => m.value === p.mode_paiement)?.label || p.mode_paiement}</span>
                          <span className="font-medium text-green-600">+{p.montant?.toFixed(2)}€</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Quick amount buttons */}
              <div className="space-y-2">
                <Label>Montant du paiement</Label>
                <div className="flex gap-2 mb-2">
                  <Button 
                    type="button"
                    variant="outline" 
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={() => setPaiementForm({...paiementForm, montant: getRestantAPayer(selectedFacture).toFixed(2)})}
                  >
                    Solde complet
                  </Button>
                  <Button 
                    type="button"
                    variant="outline" 
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={() => setPaiementForm({...paiementForm, montant: (getRestantAPayer(selectedFacture) / 2).toFixed(2)})}
                  >
                    50%
                  </Button>
                  <Button 
                    type="button"
                    variant="outline" 
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={() => setPaiementForm({...paiementForm, montant: ''})}
                  >
                    Autre
                  </Button>
                </div>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={getRestantAPayer(selectedFacture)}
                    value={paiementForm.montant}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      const max = getRestantAPayer(selectedFacture);
                      if (val > max) {
                        setPaiementForm({...paiementForm, montant: max.toFixed(2)});
                      } else {
                        setPaiementForm({...paiementForm, montant: e.target.value});
                      }
                    }}
                    placeholder="0.00"
                    className="pr-8 text-lg font-medium"
                    data-testid="payment-amount"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400">€</span>
                </div>
                {parseFloat(paiementForm.montant) > 0 && (
                  <p className="text-xs text-stone-500">
                    Reste après ce paiement: <strong className="text-orange-600">{(getRestantAPayer(selectedFacture) - parseFloat(paiementForm.montant || 0)).toFixed(2)}€</strong>
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Mode de paiement</Label>
                <div className="grid grid-cols-4 gap-2">
                  {paymentMethods.map(m => (
                    <Button
                      key={m.value}
                      type="button"
                      variant={paiementForm.mode_paiement === m.value ? "default" : "outline"}
                      size="sm"
                      className={`text-xs ${paiementForm.mode_paiement === m.value ? 'bg-[#2E4A3D] text-white' : ''}`}
                      onClick={() => setPaiementForm({...paiementForm, mode_paiement: m.value})}
                    >
                      {m.label}
                    </Button>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Notes (optionnel)</Label>
                <Textarea
                  value={paiementForm.notes}
                  onChange={(e) => setPaiementForm({...paiementForm, notes: e.target.value})}
                  placeholder="Référence chèque, numéro de virement..."
                  rows={2}
                  className="text-sm"
                />
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowPaiementDialog(false)} className="flex-1">
                  Annuler
                </Button>
                <Button 
                  onClick={handleEnregistrerPaiement} 
                  className="flex-1 btn-primary"
                  disabled={!paiementForm.montant || parseFloat(paiementForm.montant) <= 0}
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Enregistrer {paiementForm.montant ? `${paiementForm.montant}€` : ''}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal création avoir */}
      <Dialog open={showAvoirModal} onOpenChange={setShowAvoirModal}>
        <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-[#2E4A3D]">
              Annuler et créer un avoir
            </DialogTitle>
          </DialogHeader>
          
          {avoirFacture && (
            <div className="space-y-4 mt-2">
              {/* Facture source info */}
              <div className="bg-gradient-to-r from-stone-50 to-stone-100 p-4 rounded-lg border">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-stone-500">Facture d'origine</p>
                    <p className="font-semibold text-[#2E4A3D]">N° {avoirFacture.numero_facture}</p>
                    <p className="text-sm text-stone-500">{getUserName(avoirFacture.cavalier_id)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-[#2E4A3D]">{avoirFacture.total_ttc?.toFixed(2)}€</p>
                    <Badge className={statusConfig[avoirFacture.statut]?.color}>
                      {statusConfig[avoirFacture.statut]?.label}
                    </Badge>
                  </div>
                </div>
                
                {/* Montants */}
                <div className="mt-3 pt-3 border-t border-stone-200 grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-stone-500">Déjà payé:</span>
                    <span className="ml-2 font-medium text-green-600">
                      {/* Si statut payee mais montant_paye=0, afficher le total car c'est une facture marquée payée manuellement */}
                      {(avoirFacture.statut === 'payee' && !avoirFacture.montant_paye 
                        ? avoirFacture.total_ttc 
                        : (avoirFacture.montant_paye || 0)
                      ).toFixed(2)}€
                    </span>
                  </div>
                  <div>
                    <span className="text-stone-500">Restant:</span>
                    <span className="ml-2 font-medium text-orange-600">
                      {(avoirFacture.statut === 'payee' 
                        ? 0 
                        : getRestantAPayer(avoirFacture)
                      ).toFixed(2)}€
                    </span>
                  </div>
                </div>
              </div>

              {/* Explication */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-amber-800 mb-2">Un avoir sera créé avec :</p>
                <ul className="list-disc list-inside text-amber-700 space-y-1">
                  <li>Le même montant en négatif (-{avoirFacture.total_ttc?.toFixed(2)}€)</li>
                  <li>Une référence à la facture N° {avoirFacture.numero_facture}</li>
                  {avoirFacture.statut === 'payee' ? (
                    <li className="text-red-600 font-medium">Statut: En attente (remboursement de {avoirFacture.total_ttc?.toFixed(2)}€ à effectuer)</li>
                  ) : (
                    <li className="text-emerald-600 font-medium">Statut: Soldé (pas de remboursement)</li>
                  )}
                </ul>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowAvoirModal(false)}
                  disabled={isCreatingAvoir}
                >
                  Annuler
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  onClick={confirmCreerAvoir}
                  disabled={isCreatingAvoir}
                >
                  {isCreatingAvoir ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Ban className="w-4 h-4 mr-2" />
                  )}
                  Créer l'avoir
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Invoice detail modal */}
      {selectedFacture && !showPaiementDialog && (
        <Dialog open={!!selectedFacture} onOpenChange={() => setSelectedFacture(null)}>
          <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif">
                {selectedFacture.type_document === 'avoir' ? 'Avoir' : 'Facture'} {selectedFacture.numero_facture || '(Brouillon)'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-500">Cavalier</p>
                  <p className="font-medium">{getUserName(selectedFacture.cavalier_id)}</p>
                </div>
                <div className="text-right">
                  {selectedFacture.type_document === 'avoir' && (
                    <Badge className="bg-red-100 text-red-700 mb-1">AVOIR</Badge>
                  )}
                  <Badge className={statusConfig[selectedFacture.statut]?.color}>
                    {statusConfig[selectedFacture.statut]?.label}
                  </Badge>
                </div>
              </div>
              
              {/* Reference to source invoice for avoirs */}
              {selectedFacture.type_document === 'avoir' && selectedFacture.facture_source_numero && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm space-y-2">
                  <p className="text-red-700">
                    <strong>Référence :</strong> Avoir pour facture n°{selectedFacture.facture_source_numero}
                  </p>
                  {/* Affichage du solde */}
                  <div className="pt-2 border-t border-red-200">
                    <div className="flex justify-between">
                      <span className="text-stone-600">Montant facture origine:</span>
                      <span className="font-medium">{Math.abs(selectedFacture.total_ttc || 0).toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-600">Déjà payé sur facture:</span>
                      <span className="font-medium text-green-600">{(selectedFacture.montant_paye_origine || 0).toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between font-medium pt-1 border-t border-red-200 mt-1">
                      <span className="text-red-700">Remboursement à effectuer:</span>
                      <span className={selectedFacture.montant_paye_origine > 0 ? "text-orange-600" : "text-emerald-600"}>
                        {selectedFacture.montant_paye_origine > 0 
                          ? `${(selectedFacture.montant_paye_origine || 0).toFixed(2)}€` 
                          : "Aucun (soldé)"
                        }
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <p className="text-sm text-stone-500">Émission</p>
                  <p className="font-medium">{format(new Date(selectedFacture.date_emission), 'dd/MM/yyyy')}</p>
                </div>
                <div>
                  <p className="text-sm text-stone-500">Échéance</p>
                  <p className="font-medium">{format(new Date(selectedFacture.date_echeance), 'dd/MM/yyyy')}</p>
                </div>
              </div>
              
              <div className="pt-4 border-t">
                <p className="text-sm text-stone-500 mb-2">Détail</p>
                <div className="space-y-2">
                  {selectedFacture.lignes?.map((ligne, index) => (
                    <div key={index} className="flex justify-between text-sm p-2 bg-stone-50 rounded">
                      <span>{ligne.prestation} (x{ligne.quantite})</span>
                      <span className="font-medium">{ligne.total_ligne?.toFixed(2)}€</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="pt-4 border-t">
                <div className="flex justify-between text-sm">
                  <span>Total HT</span>
                  <span>{selectedFacture.total_ht?.toFixed(2)}€</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>TVA (20%)</span>
                  <span>{selectedFacture.tva?.toFixed(2)}€</span>
                </div>
                <div className="flex justify-between text-lg font-bold mt-2 pt-2 border-t">
                  <span>Total TTC</span>
                  <span className="text-[#2E4A3D]">{selectedFacture.total_ttc?.toFixed(2)}€</span>
                </div>
                
                {/* Payment info */}
                {selectedFacture.montant_paye > 0 && (
                  <div className="mt-3 pt-3 border-t space-y-1">
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Payé</span>
                      <span>{selectedFacture.montant_paye?.toFixed(2)}€</span>
                    </div>
                    {getRestantAPayer(selectedFacture) > 0 && (
                      <div className="flex justify-between text-sm font-medium text-orange-600">
                        <span>Reste à payer</span>
                        <span>{getRestantAPayer(selectedFacture).toFixed(2)}€</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Actions based on status */}
              <div className="flex flex-wrap gap-2 pt-4">
                <Button 
                  variant="outline"
                  className="flex-1"
                  onClick={() => handleDownloadPDF(selectedFacture.id)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  PDF
                </Button>
                
                {/* Info: Notifications are automatic - no manual button needed */}
                
                {/* Draft: Edit, Send or Delete */}
                {isInstructor() && selectedFacture.statut === 'brouillon' && (
                  <>
                    <Button 
                      variant="outline"
                      className="flex-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                      onClick={() => { handleEditFacture(selectedFacture); setSelectedFacture(null); }}
                      data-testid="edit-draft-btn"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Modifier
                    </Button>
                    <Button 
                      className="btn-primary flex-1"
                      onClick={() => handleEnvoyerFacture(selectedFacture.id)}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Envoyer
                    </Button>
                    <Button 
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => handleDeleteFacture(selectedFacture.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
                
                {/* Sent: Payment, Mark late, Create avoir (for numbered) */}
                {isInstructor() && ['en_attente', 'payee_partiellement'].includes(selectedFacture.statut) && (
                  <>
                    {selectedFacture.type_document === 'avoir' ? (
                      /* Pour les avoirs : bouton Solder */
                      <Button 
                        className="btn-primary flex-1"
                        onClick={() => handleSolderAvoir(selectedFacture.id)}
                        title="Solder l'avoir (marquer comme remboursé/clôturé)"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Solder l'avoir
                      </Button>
                    ) : (
                      /* Pour les factures normales : bouton Paiement */
                      <>
                        <Button 
                          className="btn-primary flex-1"
                          onClick={() => setShowPaiementDialog(true)}
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          Paiement
                        </Button>
                        <Button 
                          variant="outline"
                          className="text-orange-600 border-orange-200 hover:bg-orange-50"
                          onClick={() => handleMarquerEnRetard(selectedFacture.id)}
                        >
                          <AlertTriangle className="w-4 h-4" />
                        </Button>
                        {selectedFacture.numero_facture ? (
                          <Button 
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleCreerAvoir(selectedFacture.id)}
                            data-testid="create-avoir-btn"
                            title="Annuler et créer un avoir"
                          >
                            <FileText className="w-4 h-4 mr-1" />
                            Avoir
                          </Button>
                        ) : (
                          <Button 
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleAnnulerFacture(selectedFacture.id)}
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                        )}
                      </>
                    )}
                  </>
                )}
                
                {/* Late: Payment, Create avoir (for numbered) */}
                {isInstructor() && selectedFacture.statut === 'en_retard' && (
                  <>
                    <Button 
                      className="btn-primary flex-1"
                      onClick={() => setShowPaiementDialog(true)}
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Paiement
                    </Button>
                    {selectedFacture.numero_facture && selectedFacture.type_document !== 'avoir' ? (
                      <Button 
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleCreerAvoir(selectedFacture.id)}
                        data-testid="create-avoir-late-btn"
                        title="Annuler et créer un avoir"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Avoir
                      </Button>
                    ) : selectedFacture.type_document !== 'avoir' ? (
                      <Button 
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleAnnulerFacture(selectedFacture.id)}
                      >
                        <Ban className="w-4 h-4" />
                      </Button>
                    ) : null}
                  </>
                )}
                
                {/* Paid: Create avoir (for refund) */}
                {isInstructor() && selectedFacture.statut === 'payee' && selectedFacture.type_document !== 'avoir' && (
                  <>
                    {selectedFacture.numero_facture && (
                      <Button 
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleCreerAvoir(selectedFacture.id)}
                        data-testid="create-avoir-paid-btn"
                        title="Annuler et créer un avoir (remboursement)"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Annuler et créer un avoir
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Draft Invoice Dialog */}
      <Dialog open={!!editingFacture} onOpenChange={(open) => !open && setEditingFacture(null)}>
        <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Modifier la facture {editingFacture?.numero_facture}
            </DialogTitle>
          </DialogHeader>
          
          {editingFacture && (
            <div className="space-y-4 mt-4">
              {/* Cavalier info (read-only) */}
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="text-sm text-stone-500">Cavalier</p>
                <p className="font-medium">{getUserName(editingFacture.cavalier_id)}</p>
              </div>
              
              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Date d&apos;émission</Label>
                  <Input
                    type="date"
                    value={editingFacture.date_emission?.split('T')[0] || ''}
                    onChange={(e) => setEditingFacture({...editingFacture, date_emission: e.target.value})}
                    className="input-field"
                  />
                </div>
                <div>
                  <Label className="form-label">Date d&apos;échéance</Label>
                  <Input
                    type="date"
                    value={editingFacture.date_echeance?.split('T')[0] || ''}
                    onChange={(e) => setEditingFacture({...editingFacture, date_echeance: e.target.value})}
                    className="input-field"
                  />
                </div>
              </div>
              
              {/* Lines */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="form-label">Lignes de facturation</Label>
                  <Button variant="outline" size="sm" onClick={handleAddEditLigne}>
                    <Plus className="w-4 h-4 mr-1" /> Ajouter
                  </Button>
                </div>
                
                {/* Helper text */}
                <p className="text-xs text-stone-500 mb-3">
                  Sélectionnez une prestation du barème ou saisissez librement la description et le prix TTC.
                </p>
                
                <div className="space-y-2">
                  {editingFacture.lignes.map((ligne, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-end p-3 bg-stone-50 rounded-lg">
                      <div className="col-span-5">
                        <Label className="text-xs text-stone-500 mb-1 block">Prestation</Label>
                        <div className="flex gap-1">
                          <Input
                            placeholder="Description libre ou sélection →"
                            value={ligne.prestation || ''}
                            onChange={(e) => handleUpdateEditLigne(index, 'prestation', e.target.value)}
                            className="input-field text-sm flex-1"
                            data-testid={`edit-ligne-prestation-${index}`}
                          />
                          {prestations.length > 0 && (
                            <Select onValueChange={(v) => handleSelectEditPrestation(index, v)}>
                              <SelectTrigger className="w-10 h-9 p-0 justify-center border-amber-300 bg-amber-50 hover:bg-amber-100" title="Choisir depuis le barème">
                                <span className="text-amber-700">▼</span>
                              </SelectTrigger>
                              <SelectContent>
                                <div className="px-2 py-1.5 text-xs font-medium text-stone-500 border-b">
                                  Barème actif (prix TTC)
                                </div>
                                {prestations.map(p => (
                                  <SelectItem key={p.id} value={p.id}>
                                    <span className="flex justify-between gap-2 w-full">
                                      <span>{p.label}</span>
                                      <span className="text-amber-600 font-bold">{p.prix}€ TTC</span>
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs text-stone-500 mb-1 block">Qté</Label>
                        <Input
                          type="number"
                          min="1"
                          placeholder="1"
                          value={ligne.quantite || 1}
                          onChange={(e) => handleUpdateEditLigne(index, 'quantite', parseInt(e.target.value) || 1)}
                          className="input-field text-sm"
                          data-testid={`edit-ligne-quantite-${index}`}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs text-stone-500 mb-1 block">Prix TTC</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={ligne.prix_unitaire || 0}
                          onChange={(e) => handleUpdateEditLigne(index, 'prix_unitaire', parseFloat(e.target.value) || 0)}
                          className="input-field text-sm"
                          data-testid={`edit-ligne-prix-${index}`}
                        />
                      </div>
                      <div className="col-span-2 text-right">
                        <Label className="text-xs text-stone-500 mb-1 block">Total TTC</Label>
                        <p className="font-medium h-9 flex items-center justify-end">
                          {(ligne.total_ligne || 0).toFixed(2)}€
                        </p>
                      </div>
                      <div className="col-span-1 flex items-end justify-center">
                        {editingFacture.lignes.length > 1 && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500 h-9 w-9 p-0"
                            onClick={() => handleRemoveEditLigne(index)}
                            data-testid={`edit-remove-ligne-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="flex justify-end mt-4 pt-4 border-t">
                  <div className="text-right">
                    <p className="text-sm text-stone-500">Total HT</p>
                    <p className="text-lg text-stone-600">{(getEditTotal() / 1.2).toFixed(2)}€</p>
                    <p className="text-sm text-stone-500 mt-1">TVA (20%)</p>
                    <p className="text-sm text-stone-600">{(getEditTotal() - getEditTotal() / 1.2).toFixed(2)}€</p>
                    <p className="text-sm font-semibold text-[#2E4A3D] mt-2">Total TTC</p>
                    <p className="text-2xl font-bold text-[#2E4A3D]">{getEditTotal().toFixed(2)}€</p>
                  </div>
                </div>
              </div>
              
              {/* Notes */}
              <div>
                <Label className="form-label">Notes</Label>
                <Textarea
                  value={editingFacture.notes || ''}
                  onChange={(e) => setEditingFacture({...editingFacture, notes: e.target.value})}
                  placeholder="Notes ou commentaires..."
                  className="input-field min-h-[60px]"
                />
              </div>
              
              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setEditingFacture(null)}
                >
                  Annuler
                </Button>
                <Button 
                  className="btn-primary flex-1"
                  onClick={handleSaveEditFacture}
                  disabled={isSavingEdit}
                  data-testid="save-edit-btn"
                >
                  {isSavingEdit ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Enregistrer
                </Button>
                
                {/* Info: Notifications are automatic */}
                <div className="flex items-center gap-1 text-xs text-stone-500" title="Le cavalier sera notifié automatiquement">
                  <Bell className="w-3 h-3" />
                  <span>Notif. auto</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog for managing course billing status */}
      <Dialog open={showCoursFacturationDialog} onOpenChange={setShowCoursFacturationDialog}>
        <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5" />
              Gérer les cours facturés - {coursFacturationData?.cavalier_nom}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-stone-500">
              Marquez manuellement les cours comme facturés ou non facturés. 
              Les cours marqués manuellement ont priorité sur la détection automatique.
            </p>
            
            {loadingCoursFacturation ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
              </div>
            ) : coursFacturationData?.cours?.length === 0 ? (
              <div className="text-center py-8 text-stone-500">
                Aucun cours passé trouvé pour ce cavalier
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-stone-50">
                      <TableHead className="w-24">Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Activité</TableHead>
                      <TableHead className="w-32 text-center">Statut</TableHead>
                      <TableHead className="w-28 text-center">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {coursFacturationData?.cours?.map((cours) => (
                      <TableRow key={cours.id} className={cours.is_invoiced ? 'bg-green-50' : ''}>
                        <TableCell className="font-medium">{cours.date}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {cours.type_cours}
                          </Badge>
                        </TableCell>
                        <TableCell>{cours.type_activite}</TableCell>
                        <TableCell className="text-center">
                          {cours.is_invoiced ? (
                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Facturé
                              {cours.source === 'manuel' && ' (manuel)'}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-stone-500">
                              Non facturé
                              {cours.source === 'manuel' && ' (manuel)'}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className={`h-7 px-2 text-xs ${cours.is_invoiced ? 'text-red-600 hover:text-red-700 hover:bg-red-50' : 'text-green-600 hover:text-green-700 hover:bg-green-50'}`}
                              onClick={() => toggleCoursFacturation(cours.id, cours.is_invoiced)}
                            >
                              {cours.is_invoiced ? 'Défacturer' : 'Facturer'}
                            </Button>
                            {cours.source === 'manuel' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs text-stone-500 hover:text-stone-700"
                                onClick={() => removeCoursFacturationMark(cours.id)}
                                title="Supprimer le marquage manuel"
                              >
                                <XCircle className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            
            <div className="flex justify-between items-center pt-4 border-t">
              <p className="text-xs text-stone-500">
                {coursFacturationData?.cours?.filter(c => c.is_invoiced).length || 0} cours facturés sur {coursFacturationData?.cours?.length || 0}
              </p>
              <Button variant="outline" onClick={() => setShowCoursFacturationDialog(false)}>
                Fermer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </TabsContent>
      
      {/* DECOMPTE TAB CONTENT */}
      <TabsContent value="decompte">
        {isAdmin() ? (
          <DecompteTab 
            onInvoiceCreated={fetchData} 
            isActive={mainTab === 'decompte'}
            facturationEnabled={facturationEnabled}
          />
        ) : (
          // Vue cavalier : décompte d'activité simplifié
          <CavalierDecompteView />
        )}
      </TabsContent>
      </Tabs>
    </div>
  );
};
